/* Mocked in-memory implementation of OrdersClient for local development and UI wiring. */

import { CancelToken } from 'axios';
import { IOrdersClient } from './IOrdersClient';
import {
  Result,
  PaginatedList,
  Order,
  OrderDetails,
  OrderType,
  DeliverySchedule,
  Supplier,
  Category,
  OrderForm,
  ShippingDetailsRequest,
  ContainerFillResult,
  FileResponse,
  FileRequest,
  CreateOrderRequest,
  AddOrderItemRequest,
  DeleteOrderItemsRequest,
  OrderUpdate,
  OrderFormUpdate,
  OrdersFilterOptions,
  Item,
  ItemDocument,
  ItemsFilterOptions,
  ItemFilterParams,
  Catalogue,
  CatalogueFilterOptions,
  CatalogueProducts,
  CatalogueProductsFilterOptions,
  ProductAttributeModel,
  ProductAttributesRequest,
  CurrencyConversionContent,
  ExchangeRateRequest,
  News,
  OutOfStockUrgentAction,
  DefaultOrderSettings,
  DeliveryMethodListModel,
  Instance,
  InstanceFilterOptions,
  Market,
  MarketFilterOptions,
  MarketOrderType,
  MarketOrderTypeFilterOptions,
  MarketSupplier,
  MarketSupplierFilterOptions,
  MarketCatalogue,
  MarketCatalogueFilterOptions,
  MarketStore,
  MarketStoreFilterOptions,
  MarketStoreUploadRequest,
  MarketStoreUploadResult,
  UomType,
  UomTypeFilterOptions,
  MarketCategory,
  MarketCategoryFilterOptions,
  MarketItem,
  MarketItemFilterOptions,
  MarketItemUploadRequest,
  MarketItemUploadResult,
  AutoshipTemplate,
  AutoshipTemplateModel,
  AutoshipmentTemplatesFilterOptions,
  AutoshipSchedule,
  AutoshipScheduleModel,
  AutoshipmentSchedulesFilterOptions,
  StoreAvailableODS,
  IdNameModel,
  RetailerEdnOrderModel,
  OrderlySystem,
  DeliveryScheduleFilterOptions,
  DeliveryScheduleModel,
  BulkODSDateUpdateModel,
  AddODSModel,
  ODSImportResultModel,
  ForecastOrdersReportRequest,
} from './ordersTypes';

/* ---- Date helpers (keeps mock data evergreen) ---- */

function formatDate(d: Date): string {
  return d.toISOString().substring(0, 10);
}

function formatDateDMY(d: Date): string {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

function offsetDate(days: number): Date {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d;
}

/* ---- Mock Data ---- */

const OrderStatusId = {
  Draft: 1,
  Submitted: 2,
  Confirmed: 3,
  Shipped: 4,
  Cancelled: 5,
};

const mockOrders: Order[] = [
  {
    id: 1001,
    statusId: OrderStatusId.Draft,
    status: 'Draft',
    emergencyOrder: false,
    submitted: false,
    orderTypeId: 1,
    orderType: 'Fresh & Daily',
    deliveryDate: formatDate(offsetDate(3)),
    cutOffDate: formatDate(offsetDate(1)),
    cutOffTime: '18:00',
    totalCost: 245.50,
    totalQuantity: 24,
    supplierName: 'Fresh Foods Ltd',
    externallyCreatedOrder: false,
    deliveryDateUpdated: false,
    isAutoship: false,
  },
  {
    id: 1002,
    statusId: OrderStatusId.Submitted,
    status: 'Submitted',
    emergencyOrder: false,
    submitted: true,
    orderTypeId: 2,
    orderType: 'Ambient',
    deliveryDate: formatDate(offsetDate(6)),
    cutOffDate: formatDate(offsetDate(2)),
    cutOffTime: '12:00',
    totalCost: 892.75,
    totalQuantity: 87,
    supplierName: 'Ambient Supplies Co',
    externallyCreatedOrder: false,
    deliveryDateUpdated: false,
    isAutoship: false,
  },
  {
    id: 1003,
    statusId: OrderStatusId.Confirmed,
    status: 'Confirmed',
    emergencyOrder: true,
    submitted: true,
    orderTypeId: 1,
    orderType: 'Fresh & Daily',
    deliveryDate: formatDate(offsetDate(2)),
    cutOffDate: formatDate(offsetDate(0)),
    cutOffTime: '10:00',
    totalCost: 156.20,
    totalQuantity: 15,
    supplierName: 'Fresh Foods Ltd',
    externallyCreatedOrder: false,
    deliveryDateUpdated: false,
    isAutoship: false,
  },
  {
    id: 1004,
    statusId: OrderStatusId.Shipped,
    status: 'Shipped',
    emergencyOrder: false,
    submitted: true,
    orderTypeId: 3,
    orderType: 'Frozen',
    deliveryDate: formatDate(offsetDate(1)),
    cutOffDate: formatDate(offsetDate(-2)),
    cutOffTime: '14:00',
    totalCost: 567.80,
    totalQuantity: 42,
    supplierName: 'Arctic Frozen Foods',
    externallyCreatedOrder: false,
    deliveryDateUpdated: false,
    isAutoship: true,
  },
];

const mockOrderDetails: OrderDetails = {
  id: 1001,
  statusId: OrderStatusId.Draft,
  status: 'Draft',
  emergencyOrder: false,
  submitted: false,
  orderTypeId: 1,
  orderType: 'Fresh & Daily',
  deliveryDate: formatDate(offsetDate(3)),
  cutOffDate: formatDate(offsetDate(1)),
  cutOffTime: '18:00',
  supplierNumber: 'SUP001',
  supplierName: 'Fresh Foods Ltd',
  cutOffTimeLeft: { days: 1, hours: 8, minutes: 30 },
  isAutoship: false,
  items: [
    {
      id: 1,
      itemId: 101,
      sku: 'SKU-MILK-001',
      name: 'Whole Milk 2L',
      primaryUom: 'Each',
      orderableUom: 'Case',
      price: 2.50,
      gbpPrice: 2.50,
      categoryId: 1,
      packSize: '6x2L',
      category: 'Dairy',
      stockOnHand: 12,
      stockOnOrder: 24,
      quantity: 4,
      confirmedQuantity: 0,
      shippedQuantity: 0,
      imageId: 1,
      thumbnailUrl: '',
      maxQuantity: 100,
      minQuantity: 1,
      gtin: '1234567890123',
    },
    {
      id: 2,
      itemId: 102,
      sku: 'SKU-BREAD-001',
      name: 'White Sliced Bread 800g',
      primaryUom: 'Each',
      orderableUom: 'Case',
      price: 1.20,
      gbpPrice: 1.20,
      categoryId: 2,
      packSize: '10x800g',
      category: 'Bakery',
      stockOnHand: 8,
      stockOnOrder: 20,
      quantity: 10,
      confirmedQuantity: 0,
      shippedQuantity: 0,
      imageId: 2,
      thumbnailUrl: '',
      maxQuantity: 50,
      minQuantity: 1,
      gtin: '1234567890124',
    },
  ],
  approvalStatus: '',
  rejectionReason: '',
  approvalComments: '',
  poNumber: '',
  approvalDate: '',
  documents: [],
  amendmentDetails: '',
};

const mockOrderTypes: OrderType[] = [
  { id: 1, name: 'Fresh & Daily' },
  { id: 2, name: 'Ambient' },
  { id: 3, name: 'Frozen' },
  { id: 4, name: 'Direct Store Delivery' },
];

const mockSuppliers: Supplier[] = [
  { id: 1, name: 'Fresh Foods Ltd', number: 'SUP001' },
  { id: 2, name: 'Ambient Supplies Co', number: 'SUP002' },
  { id: 3, name: 'Arctic Frozen Foods', number: 'SUP003' },
  { id: 4, name: 'DSD Supplier Inc', number: 'SUP004' },
];

const mockDeliverySchedules: DeliverySchedule[] = [
  // Order Type 1 - Fresh & Daily
  {
    id: 1,
    customerId: 1,
    orderType: 1,
    cutOffDate: formatDateDMY(offsetDate(1)),
    cutOffTime: '18:00',
    shipDate: formatDateDMY(offsetDate(2)),
    deliveryDate: formatDateDMY(offsetDate(3)),
    supplier: 'SUP001',
    distributionCenter: 'DC-EAST',
  },
  {
    id: 2,
    customerId: 1,
    orderType: 1,
    cutOffDate: formatDateDMY(offsetDate(4)),
    cutOffTime: '18:00',
    shipDate: formatDateDMY(offsetDate(5)),
    deliveryDate: formatDateDMY(offsetDate(6)),
    supplier: 'SUP001',
    distributionCenter: 'DC-EAST',
  },
  {
    id: 3,
    customerId: 1,
    orderType: 1,
    cutOffDate: formatDateDMY(offsetDate(7)),
    cutOffTime: '18:00',
    shipDate: formatDateDMY(offsetDate(8)),
    deliveryDate: formatDateDMY(offsetDate(10)),
    supplier: 'SUP001',
    distributionCenter: 'DC-EAST',
  },
  {
    id: 4,
    customerId: 1,
    orderType: 1,
    cutOffDate: formatDateDMY(offsetDate(10)),
    cutOffTime: '18:00',
    shipDate: formatDateDMY(offsetDate(11)),
    deliveryDate: formatDateDMY(offsetDate(13)),
    supplier: 'SUP001',
    distributionCenter: 'DC-EAST',
  },
  // Order Type 2 - Ambient
  {
    id: 5,
    customerId: 1,
    orderType: 2,
    cutOffDate: formatDateDMY(offsetDate(2)),
    cutOffTime: '12:00',
    shipDate: formatDateDMY(offsetDate(4)),
    deliveryDate: formatDateDMY(offsetDate(6)),
    supplier: 'SUP002',
    distributionCenter: 'DC-CENTRAL',
  },
  {
    id: 6,
    customerId: 1,
    orderType: 2,
    cutOffDate: formatDateDMY(offsetDate(7)),
    cutOffTime: '12:00',
    shipDate: formatDateDMY(offsetDate(9)),
    deliveryDate: formatDateDMY(offsetDate(11)),
    supplier: 'SUP002',
    distributionCenter: 'DC-CENTRAL',
  },
  // Order Type 3 - Frozen
  {
    id: 7,
    customerId: 1,
    orderType: 3,
    cutOffDate: formatDateDMY(offsetDate(-2)),
    cutOffTime: '14:00',
    shipDate: formatDateDMY(offsetDate(-1)),
    deliveryDate: formatDateDMY(offsetDate(1)),
    supplier: 'SUP003',
    distributionCenter: 'DC-NORTH',
  },
  {
    id: 8,
    customerId: 1,
    orderType: 3,
    cutOffDate: formatDateDMY(offsetDate(5)),
    cutOffTime: '14:00',
    shipDate: formatDateDMY(offsetDate(6)),
    deliveryDate: formatDateDMY(offsetDate(8)),
    supplier: 'SUP003',
    distributionCenter: 'DC-NORTH',
  },
  {
    id: 9,
    customerId: 1,
    orderType: 3,
    cutOffDate: formatDateDMY(offsetDate(12)),
    cutOffTime: '14:00',
    shipDate: formatDateDMY(offsetDate(14)),
    deliveryDate: formatDateDMY(offsetDate(16)),
    supplier: 'SUP003',
    distributionCenter: 'DC-NORTH',
  },
];

let nextOrderId = 2000;

/**
 * Mock ODS (Order Delivery Schedule) data for stores.
 * Maps store numbers to available delivery dates for ordering.
 */
export const mockStoreAvailableODS: StoreAvailableODS[] = [
  {
    customerId: 1001,
    storeNumber: '001',
    deliveryDates: [formatDate(offsetDate(3)), formatDate(offsetDate(6)), formatDate(offsetDate(10)), formatDate(offsetDate(13)), formatDate(offsetDate(17))],
  },
  {
    customerId: 1002,
    storeNumber: '002',
    deliveryDates: [formatDate(offsetDate(2)), formatDate(offsetDate(5)), formatDate(offsetDate(9)), formatDate(offsetDate(12)), formatDate(offsetDate(16))],
  },
  {
    customerId: 1003,
    storeNumber: '003',
    deliveryDates: [formatDate(offsetDate(4)), formatDate(offsetDate(7)), formatDate(offsetDate(11)), formatDate(offsetDate(14)), formatDate(offsetDate(18))],
  },
  {
    customerId: 1004,
    storeNumber: '004',
    deliveryDates: [formatDate(offsetDate(3)), formatDate(offsetDate(8)), formatDate(offsetDate(13)), formatDate(offsetDate(18))],
  },
  {
    customerId: 1005,
    storeNumber: '005',
    deliveryDates: [formatDate(offsetDate(5)), formatDate(offsetDate(10)), formatDate(offsetDate(15))],
  },
];

/**
 * Mock catalogues data for distributors.
 */
const mockCatalogues: Catalogue[] = [
  {
    id: 1,
    name: 'Core Range 2025',
    orderType: 'Standard',
    market: 'UK & Ireland',
    retailers: ['Dublin Delights Café', 'Cork Coffee House'],
    retailerCount: 125,
  },
  {
    id: 2,
    name: 'Premium Selection',
    orderType: 'Premium',
    market: 'UK & Ireland',
    retailers: ['Galway Gourmet'],
    retailerCount: 42,
  },
  {
    id: 3,
    name: 'Seasonal Specials - Winter 2025',
    orderType: 'Seasonal',
    market: 'UK & Ireland',
    retailers: ['Dublin Delights Café', 'Limerick Lunch Spot'],
    retailerCount: 78,
  },
  {
    id: 4,
    name: 'Local Suppliers - Ireland',
    orderType: 'Local',
    market: 'Ireland',
    retailers: ['Waterford Wines & More'],
    retailerCount: 34,
  },
  {
    id: 5,
    name: 'Organic & Sustainable Range',
    orderType: 'Organic',
    market: 'UK & Ireland',
    retailers: ['Dublin Delights Café', 'Cork Coffee House', 'Galway Gourmet'],
    retailerCount: 89,
  },
  {
    id: 6,
    name: 'Continental Europe Collection',
    orderType: 'International',
    market: 'Europe',
    retailers: [],
    retailerCount: 156,
  },
];

/**
 * Mock categories for catalogue products.
 */
const mockCategories: Category[] = [
  { id: 1, name: 'Dairy', display: true, showInFilters: true, showInNavigation: true, priority: 1, fullCategoryString: 'Dairy', parentId: null, parentName: '', parentFullCategoryString: '' },
  { id: 2, name: 'Bakery', display: true, showInFilters: true, showInNavigation: true, priority: 2, fullCategoryString: 'Bakery', parentId: null, parentName: '', parentFullCategoryString: '' },
  { id: 3, name: 'Coffee', display: true, showInFilters: true, showInNavigation: true, priority: 3, fullCategoryString: 'Coffee', parentId: null, parentName: '', parentFullCategoryString: '' },
  { id: 4, name: 'Condiments', display: true, showInFilters: true, showInNavigation: true, priority: 4, fullCategoryString: 'Condiments', parentId: null, parentName: '', parentFullCategoryString: '' },
  { id: 5, name: 'Frozen', display: true, showInFilters: true, showInNavigation: true, priority: 5, fullCategoryString: 'Frozen', parentId: null, parentName: '', parentFullCategoryString: '' },
  { id: 6, name: 'Beverages', display: true, showInFilters: true, showInNavigation: true, priority: 6, fullCategoryString: 'Beverages', parentId: null, parentName: '', parentFullCategoryString: '' },
  { id: 7, name: 'Fresh Produce', display: true, showInFilters: true, showInNavigation: true, priority: 7, fullCategoryString: 'Fresh Produce', parentId: null, parentName: '', parentFullCategoryString: '' },
  { id: 8, name: 'Meat & Poultry', display: true, showInFilters: true, showInNavigation: true, priority: 8, fullCategoryString: 'Meat & Poultry', parentId: null, parentName: '', parentFullCategoryString: '' },
];

/** 
 * Mock items available across the system.
 * Export these for use in other mocked clients (e.g., POD client).
 */
export const mockItems: Item[] = [
  {
    id: 101,
    sku: 'SKU-MILK-001',
    name: 'Whole Milk 2L',
    categoryId: 1,
    category: 'Dairy',
    stockOnHand: 12,
    stockOnOrder: 24,
    price: 2.50,
    gbpPrice: 2.50,
    unitPrice: 2.50,
    casePrice: 15.00,
    minimumUnitPrice: 2.00,
    por: 1.80,
    rrp: 3.00,
    packSize: '6x2L',
    primaryUom: 'Each',
    orderableUom: 'Case',
    barcode: '1234567890123',
    barcodeUom: 'Case',
    imageId: 1,
    thumbnailUrl: '',
    maxQuantity: 100,
    minQuantity: 1,
    quantityInUom: 6,
    internalId: 'MILK-2L',
    legacyProductCode: 'LPC-MILK-001',
    caseDimensions: '40x30x25cm',
    orderCategory: 'Dairy',
    productCategory: 'Fresh',
    gtin: '01234567890123',
    vatRate: 0.0,
    weight: 12.5,
    casesPerPallet: 48,
    casesPerLayer: 8,
    minShippingLife: 7,
    minShelfLife: 14,
    singlePick: false,
    caseSize: 6,
    isFrozen: false,
  },
  {
    id: 102,
    sku: 'SKU-BREAD-001',
    name: 'White Sliced Bread 800g',
    categoryId: 2,
    category: 'Bakery',
    stockOnHand: 8,
    stockOnOrder: 20,
    price: 1.20,
    gbpPrice: 1.20,
    unitPrice: 1.20,
    casePrice: 12.00,
    minimumUnitPrice: 1.00,
    por: 0.80,
    rrp: 1.50,
    packSize: '10x800g',
    primaryUom: 'Each',
    orderableUom: 'Case',
    barcode: '1234567890124',
    barcodeUom: 'Case',
    imageId: 2,
    thumbnailUrl: '',
    maxQuantity: 50,
    minQuantity: 1,
    quantityInUom: 10,
    internalId: 'BREAD-WHITE',
    legacyProductCode: 'LPC-BREAD-001',
    caseDimensions: '50x40x30cm',
    orderCategory: 'Bakery',
    productCategory: 'Fresh',
    gtin: '01234567890124',
    vatRate: 0.0,
    weight: 8.5,
    casesPerPallet: 60,
    casesPerLayer: 10,
    minShippingLife: 3,
    minShelfLife: 5,
    singlePick: false,
    caseSize: 10,
    isFrozen: false,
  },
  {
    id: 103,
    sku: 'SKU-COFFEE-001',
    name: '1kg Arabica Coffee Beans',
    categoryId: 3,
    category: 'Coffee',
    stockOnHand: 15,
    stockOnOrder: 30,
    price: 18.50,
    gbpPrice: 18.50,
    unitPrice: 18.50,
    casePrice: 74.00,
    minimumUnitPrice: 16.00,
    por: 14.00,
    rrp: 22.00,
    packSize: '4x1kg',
    primaryUom: 'Each',
    orderableUom: 'Case',
    barcode: '1234567890125',
    barcodeUom: 'Case',
    imageId: 3,
    thumbnailUrl: '',
    maxQuantity: 80,
    minQuantity: 1,
    quantityInUom: 4,
    internalId: 'COFFEE-1KG',
    legacyProductCode: 'LPC-COFFEE-001',
    caseDimensions: '35x25x20cm',
    orderCategory: 'Beverages',
    productCategory: 'Ambient',
    gtin: '01234567890125',
    vatRate: 0.2,
    weight: 4.5,
    casesPerPallet: 120,
    casesPerLayer: 20,
    minShippingLife: 90,
    minShelfLife: 180,
    singlePick: false,
    caseSize: 4,
    isFrozen: false,
  },
  {
    id: 104,
    sku: 'SKU-SUGAR-001',
    name: '1kg White Sugar',
    categoryId: 4,
    category: 'Dry Goods',
    stockOnHand: 25,
    stockOnOrder: 40,
    price: 0.85,
    gbpPrice: 0.85,
    unitPrice: 0.85,
    casePrice: 13.60,
    minimumUnitPrice: 0.75,
    por: 0.60,
    rrp: 1.20,
    packSize: '16x1kg',
    primaryUom: 'Each',
    orderableUom: 'Case',
    barcode: '1234567890126',
    barcodeUom: 'Case',
    imageId: 4,
    thumbnailUrl: '',
    maxQuantity: 120,
    minQuantity: 1,
    quantityInUom: 16,
    internalId: 'SUGAR-1KG',
    legacyProductCode: 'LPC-SUGAR-001',
    caseDimensions: '45x35x25cm',
    orderCategory: 'Dry Goods',
    productCategory: 'Ambient',
    gtin: '01234567890126',
    vatRate: 0.0,
    weight: 16.5,
    casesPerPallet: 80,
    casesPerLayer: 16,
    minShippingLife: 365,
    minShelfLife: 730,
    singlePick: false,
    caseSize: 16,
    isFrozen: false,
  },
  {
    id: 105,
    sku: 'SKU-ICECREAM-001',
    name: 'Vanilla Ice Cream 2L',
    categoryId: 5,
    category: 'Frozen',
    stockOnHand: 6,
    stockOnOrder: 12,
    price: 4.50,
    gbpPrice: 4.50,
    unitPrice: 4.50,
    casePrice: 27.00,
    minimumUnitPrice: 4.00,
    por: 3.50,
    rrp: 5.50,
    packSize: '6x2L',
    primaryUom: 'Each',
    orderableUom: 'Case',
    barcode: '1234567890127',
    barcodeUom: 'Case',
    imageId: 5,
    thumbnailUrl: '',
    maxQuantity: 40,
    minQuantity: 1,
    quantityInUom: 6,
    internalId: 'ICECREAM-2L',
    legacyProductCode: 'LPC-ICECREAM-001',
    caseDimensions: '40x30x25cm',
    orderCategory: 'Frozen',
    productCategory: 'Frozen',
    gtin: '01234567890127',
    vatRate: 0.0,
    weight: 12.8,
    casesPerPallet: 36,
    casesPerLayer: 6,
    minShippingLife: 90,
    minShelfLife: 180,
    singlePick: false,
    caseSize: 6,
    isFrozen: true,
  },
  {
    id: 106,
    sku: 'SKU-PASTA-001',
    name: 'Penne Pasta 500g',
    categoryId: 4,
    category: 'Dry Goods',
    stockOnHand: 30,
    stockOnOrder: 50,
    price: 1.30,
    gbpPrice: 1.30,
    unitPrice: 1.30,
    casePrice: 15.60,
    minimumUnitPrice: 1.10,
    por: 0.90,
    rrp: 1.80,
    packSize: '12x500g',
    primaryUom: 'Each',
    orderableUom: 'Case',
    barcode: '1234567890128',
    barcodeUom: 'Case',
    imageId: 6,
    thumbnailUrl: '',
    maxQuantity: 100,
    minQuantity: 1,
    quantityInUom: 12,
    internalId: 'PASTA-PENNE',
    legacyProductCode: 'LPC-PASTA-001',
    caseDimensions: '45x30x20cm',
    orderCategory: 'Dry Goods',
    productCategory: 'Ambient',
    gtin: '01234567890128',
    vatRate: 0.0,
    weight: 6.2,
    casesPerPallet: 100,
    casesPerLayer: 20,
    minShippingLife: 365,
    minShelfLife: 730,
    singlePick: false,
    caseSize: 12,
    isFrozen: false,
  },
  {
    id: 107,
    sku: 'SKU-FRIES-001',
    name: 'French Fries 1kg',
    categoryId: 5,
    category: 'Frozen',
    stockOnHand: 18,
    stockOnOrder: 25,
    price: 2.50,
    gbpPrice: 2.50,
    unitPrice: 2.50,
    casePrice: 22.50,
    minimumUnitPrice: 2.20,
    por: 1.80,
    rrp: 3.20,
    packSize: '9x1kg',
    primaryUom: 'Each',
    orderableUom: 'Case',
    barcode: '1234567890129',
    barcodeUom: 'Case',
    imageId: 7,
    thumbnailUrl: '',
    maxQuantity: 60,
    minQuantity: 1,
    quantityInUom: 9,
    internalId: 'FRIES-1KG',
    legacyProductCode: 'LPC-FRIES-001',
    caseDimensions: '50x35x25cm',
    orderCategory: 'Frozen',
    productCategory: 'Frozen',
    gtin: '01234567890129',
    vatRate: 0.0,
    weight: 9.5,
    casesPerPallet: 48,
    casesPerLayer: 8,
    minShippingLife: 180,
    minShelfLife: 365,
    singlePick: false,
    caseSize: 9,
    isFrozen: true,
  },
  {
    id: 108,
    sku: 'SKU-EGGS-001',
    name: 'Free Range Eggs 12pk',
    categoryId: 1,
    category: 'Dairy',
    stockOnHand: 14,
    stockOnOrder: 20,
    price: 3.20,
    gbpPrice: 3.20,
    unitPrice: 3.20,
    casePrice: 35.40,
    minimumUnitPrice: 2.80,
    por: 2.50,
    rrp: 4.00,
    packSize: '11x12pk',
    primaryUom: 'Each',
    orderableUom: 'Case',
    barcode: '1234567890130',
    barcodeUom: 'Case',
    imageId: 8,
    thumbnailUrl: '',
    maxQuantity: 50,
    minQuantity: 1,
    quantityInUom: 11,
    internalId: 'EGGS-12PK',
    legacyProductCode: 'LPC-EGGS-001',
    caseDimensions: '35x30x20cm',
    orderCategory: 'Dairy',
    productCategory: 'Fresh',
    gtin: '01234567890130',
    vatRate: 0.0,
    weight: 7.5,
    casesPerPallet: 72,
    casesPerLayer: 12,
    minShippingLife: 14,
    minShelfLife: 21,
    singlePick: false,
    caseSize: 11,
    isFrozen: false,
  },
  {
    id: 109,
    sku: 'SKU-WINE-001',
    name: 'Red Wine Merlot 750ml',
    categoryId: 6,
    category: 'Alcohol',
    stockOnHand: 10,
    stockOnOrder: 15,
    price: 12.50,
    gbpPrice: 12.50,
    unitPrice: 12.50,
    casePrice: 125.00,
    minimumUnitPrice: 11.00,
    por: 9.50,
    rrp: 15.00,
    packSize: '10x750ml',
    primaryUom: 'Each',
    orderableUom: 'Case',
    barcode: '1234567890131',
    barcodeUom: 'Case',
    imageId: 9,
    thumbnailUrl: '',
    maxQuantity: 30,
    minQuantity: 1,
    quantityInUom: 10,
    internalId: 'WINE-MERLOT',
    legacyProductCode: 'LPC-WINE-001',
    caseDimensions: '40x30x30cm',
    orderCategory: 'Alcohol',
    productCategory: 'Ambient',
    gtin: '01234567890131',
    vatRate: 0.2,
    weight: 13.5,
    casesPerPallet: 60,
    casesPerLayer: 10,
    minShippingLife: 730,
    minShelfLife: 1095,
    singlePick: false,
    caseSize: 10,
    isFrozen: false,
  },
  {
    id: 110,
    sku: 'SKU-BEER-001',
    name: 'Lager Beer 330ml 24pk',
    categoryId: 6,
    category: 'Alcohol',
    stockOnHand: 22,
    stockOnOrder: 35,
    price: 3.85,
    gbpPrice: 3.85,
    unitPrice: 3.85,
    casePrice: 38.50,
    minimumUnitPrice: 3.50,
    por: 3.00,
    rrp: 4.50,
    packSize: '10x24pk',
    primaryUom: 'Each',
    orderableUom: 'Case',
    barcode: '1234567890132',
    barcodeUom: 'Case',
    imageId: 10,
    thumbnailUrl: '',
    maxQuantity: 80,
    minQuantity: 1,
    quantityInUom: 10,
    internalId: 'BEER-LAGER',
    legacyProductCode: 'LPC-BEER-001',
    caseDimensions: '45x35x30cm',
    orderCategory: 'Alcohol',
    productCategory: 'Ambient',
    gtin: '01234567890132',
    vatRate: 0.2,
    weight: 18.5,
    casesPerPallet: 50,
    casesPerLayer: 10,
    minShippingLife: 180,
    minShelfLife: 365,
    singlePick: false,
    caseSize: 10,
    isFrozen: false,
  },
  {
    id: 111,
    sku: 'SKU-YOGURT-001',
    name: 'Greek Yogurt 500g',
    categoryId: 1,
    category: 'Dairy',
    stockOnHand: 16,
    stockOnOrder: 24,
    price: 2.40,
    gbpPrice: 2.40,
    unitPrice: 2.40,
    casePrice: 28.80,
    minimumUnitPrice: 2.10,
    por: 1.80,
    rrp: 3.00,
    packSize: '12x500g',
    primaryUom: 'Each',
    orderableUom: 'Case',
    barcode: '1234567890133',
    barcodeUom: 'Case',
    imageId: 11,
    thumbnailUrl: '',
    maxQuantity: 60,
    minQuantity: 1,
    quantityInUom: 12,
    internalId: 'YOGURT-GREEK',
    legacyProductCode: 'LPC-YOGURT-001',
    caseDimensions: '40x30x25cm',
    orderCategory: 'Dairy',
    productCategory: 'Fresh',
    gtin: '01234567890133',
    vatRate: 0.0,
    weight: 6.5,
    casesPerPallet: 80,
    casesPerLayer: 16,
    minShippingLife: 14,
    minShelfLife: 21,
    singlePick: false,
    caseSize: 12,
    isFrozen: false,
  },
  {
    id: 112,
    sku: 'SKU-CLEANER-001',
    name: 'Multi-Purpose Cleaner 5L',
    categoryId: 7,
    category: 'Non-Food',
    stockOnHand: 8,
    stockOnOrder: 12,
    price: 8.00,
    gbpPrice: 8.00,
    unitPrice: 8.00,
    casePrice: 32.00,
    minimumUnitPrice: 7.00,
    por: 6.00,
    rrp: 10.00,
    packSize: '4x5L',
    primaryUom: 'Each',
    orderableUom: 'Case',
    barcode: '1234567890134',
    barcodeUom: 'Case',
    imageId: 12,
    thumbnailUrl: '',
    maxQuantity: 40,
    minQuantity: 1,
    quantityInUom: 4,
    internalId: 'CLEANER-5L',
    legacyProductCode: 'LPC-CLEANER-001',
    caseDimensions: '40x30x35cm',
    orderCategory: 'Non-Food',
    productCategory: 'Non-Food',
    gtin: '01234567890134',
    vatRate: 0.2,
    weight: 21.0,
    casesPerPallet: 40,
    casesPerLayer: 8,
    minShippingLife: 730,
    minShelfLife: 1095,
    singlePick: false,
    caseSize: 4,
    isFrozen: false,
  },
  {
    id: 113,
    sku: 'SKU-PAPER-001',
    name: 'Paper Towels Roll 12pk',
    categoryId: 7,
    category: 'Non-Food',
    stockOnHand: 12,
    stockOnOrder: 20,
    price: 3.00,
    gbpPrice: 3.00,
    unitPrice: 3.00,
    casePrice: 24.00,
    minimumUnitPrice: 2.70,
    por: 2.20,
    rrp: 3.50,
    packSize: '8x12pk',
    primaryUom: 'Each',
    orderableUom: 'Case',
    barcode: '1234567890135',
    barcodeUom: 'Case',
    imageId: 13,
    thumbnailUrl: '',
    maxQuantity: 50,
    minQuantity: 1,
    quantityInUom: 8,
    internalId: 'PAPER-TOWEL',
    legacyProductCode: 'LPC-PAPER-001',
    caseDimensions: '50x40x40cm',
    orderCategory: 'Non-Food',
    productCategory: 'Non-Food',
    gtin: '01234567890135',
    vatRate: 0.2,
    weight: 5.5,
    casesPerPallet: 100,
    casesPerLayer: 20,
    minShippingLife: 730,
    minShelfLife: 1095,
    singlePick: false,
    caseSize: 8,
    isFrozen: false,
  },
];

/** Vapiano-specific orderable items */
const vapianoItems: Item[] = [
  { id: 201, sku: 'VAP-FLOUR-001', name: '00 Flour (Caputo) 25kg', categoryId: 4, category: 'Flour & Dry Goods', stockOnHand: 4, stockOnOrder: 8, price: 18.50, gbpPrice: 18.50, unitPrice: 18.50, casePrice: 74.00, minimumUnitPrice: 16.00, por: 14.00, rrp: 22.00, packSize: '4x25kg', primaryUom: 'Bag', orderableUom: 'Case', barcode: '2000000000001', barcodeUom: 'Case', imageId: 201, thumbnailUrl: '', maxQuantity: 20, minQuantity: 1, quantityInUom: 4, internalId: 'VAP-FLOUR', legacyProductCode: '', caseDimensions: '60x40x30cm', orderCategory: 'Dry Goods', productCategory: 'Ambient', gtin: '02000000000001', vatRate: 0.0, weight: 100, casesPerPallet: 20, casesPerLayer: 4, minShippingLife: 180, minShelfLife: 365, singlePick: false, caseSize: 4, isFrozen: false },
  { id: 202, sku: 'VAP-EGGS-001', name: 'Free Range Eggs (Large) 360pk', categoryId: 1, category: 'Dairy', stockOnHand: 3, stockOnOrder: 6, price: 42.00, gbpPrice: 42.00, unitPrice: 42.00, casePrice: 42.00, minimumUnitPrice: 38.00, por: 35.00, rrp: 48.00, packSize: '1x360', primaryUom: 'Case', orderableUom: 'Case', barcode: '2000000000002', barcodeUom: 'Case', imageId: 202, thumbnailUrl: '', maxQuantity: 10, minQuantity: 1, quantityInUom: 1, internalId: 'VAP-EGGS', legacyProductCode: '', caseDimensions: '50x40x35cm', orderCategory: 'Fresh', productCategory: 'Fresh', gtin: '02000000000002', vatRate: 0.0, weight: 22, casesPerPallet: 24, casesPerLayer: 4, minShippingLife: 14, minShelfLife: 21, singlePick: false, caseSize: 1, isFrozen: false },
  { id: 203, sku: 'VAP-GUANCIALE-001', name: 'Guanciale (Cured Pork Cheek) 2kg', categoryId: 8, category: 'Italian Charcuterie', stockOnHand: 5, stockOnOrder: 10, price: 28.50, gbpPrice: 28.50, unitPrice: 28.50, casePrice: 114.00, minimumUnitPrice: 25.00, por: 22.00, rrp: 34.00, packSize: '4x2kg', primaryUom: 'Each', orderableUom: 'Case', barcode: '2000000000003', barcodeUom: 'Case', imageId: 203, thumbnailUrl: '', maxQuantity: 15, minQuantity: 1, quantityInUom: 4, internalId: 'VAP-GUANCIALE', legacyProductCode: '', caseDimensions: '40x30x20cm', orderCategory: 'Chilled Meat', productCategory: 'Fresh', gtin: '02000000000003', vatRate: 0.0, weight: 8.5, casesPerPallet: 48, casesPerLayer: 8, minShippingLife: 30, minShelfLife: 60, singlePick: false, caseSize: 4, isFrozen: false },
  { id: 204, sku: 'VAP-PECORINO-001', name: 'Pecorino Romano DOP 5kg', categoryId: 1, category: 'Italian Cheese', stockOnHand: 2, stockOnOrder: 4, price: 65.00, gbpPrice: 65.00, unitPrice: 65.00, casePrice: 130.00, minimumUnitPrice: 58.00, por: 50.00, rrp: 75.00, packSize: '2x5kg', primaryUom: 'Each', orderableUom: 'Case', barcode: '2000000000004', barcodeUom: 'Case', imageId: 204, thumbnailUrl: '', maxQuantity: 8, minQuantity: 1, quantityInUom: 2, internalId: 'VAP-PECORINO', legacyProductCode: '', caseDimensions: '40x30x25cm', orderCategory: 'Cheese', productCategory: 'Fresh', gtin: '02000000000004', vatRate: 0.0, weight: 10.5, casesPerPallet: 40, casesPerLayer: 8, minShippingLife: 60, minShelfLife: 90, singlePick: false, caseSize: 2, isFrozen: false },
  { id: 205, sku: 'VAP-MOZZ-001', name: 'Fior di Latte Mozzarella 1kg', categoryId: 1, category: 'Italian Cheese', stockOnHand: 8, stockOnOrder: 16, price: 12.80, gbpPrice: 12.80, unitPrice: 12.80, casePrice: 76.80, minimumUnitPrice: 11.00, por: 9.50, rrp: 15.00, packSize: '6x1kg', primaryUom: 'Case', orderableUom: 'Case', barcode: '2000000000005', barcodeUom: 'Case', imageId: 205, thumbnailUrl: '', maxQuantity: 25, minQuantity: 1, quantityInUom: 6, internalId: 'VAP-MOZZ', legacyProductCode: '', caseDimensions: '35x25x20cm', orderCategory: 'Cheese', productCategory: 'Fresh', gtin: '02000000000005', vatRate: 0.0, weight: 6.5, casesPerPallet: 60, casesPerLayer: 12, minShippingLife: 14, minShelfLife: 21, singlePick: false, caseSize: 6, isFrozen: false },
  { id: 206, sku: 'VAP-TOMATO-001', name: 'San Marzano Tomatoes 2.5kg', categoryId: 4, category: 'Tinned Goods', stockOnHand: 12, stockOnOrder: 24, price: 4.80, gbpPrice: 4.80, unitPrice: 4.80, casePrice: 28.80, minimumUnitPrice: 4.00, por: 3.50, rrp: 5.80, packSize: '6x2.5kg', primaryUom: 'Case', orderableUom: 'Case', barcode: '2000000000006', barcodeUom: 'Case', imageId: 206, thumbnailUrl: '', maxQuantity: 40, minQuantity: 1, quantityInUom: 6, internalId: 'VAP-TOMATO', legacyProductCode: '', caseDimensions: '40x30x25cm', orderCategory: 'Tinned', productCategory: 'Ambient', gtin: '02000000000006', vatRate: 0.0, weight: 15.5, casesPerPallet: 60, casesPerLayer: 10, minShippingLife: 365, minShelfLife: 730, singlePick: false, caseSize: 6, isFrozen: false },
  { id: 207, sku: 'VAP-OLIVE-001', name: 'Extra Virgin Olive Oil 5L', categoryId: 4, category: 'Oils & Vinegars', stockOnHand: 3, stockOnOrder: 6, price: 24.00, gbpPrice: 24.00, unitPrice: 24.00, casePrice: 72.00, minimumUnitPrice: 21.00, por: 18.00, rrp: 28.00, packSize: '3x5L', primaryUom: 'Each', orderableUom: 'Case', barcode: '2000000000007', barcodeUom: 'Case', imageId: 207, thumbnailUrl: '', maxQuantity: 12, minQuantity: 1, quantityInUom: 3, internalId: 'VAP-OLIVE', legacyProductCode: '', caseDimensions: '45x30x25cm', orderCategory: 'Oils', productCategory: 'Ambient', gtin: '02000000000007', vatRate: 0.0, weight: 15.5, casesPerPallet: 40, casesPerLayer: 8, minShippingLife: 365, minShelfLife: 730, singlePick: false, caseSize: 3, isFrozen: false },
  { id: 208, sku: 'VAP-PROSECCO-001', name: 'Prosecco DOC 750ml', categoryId: 6, category: 'Wines & Spirits', stockOnHand: 6, stockOnOrder: 12, price: 6.50, gbpPrice: 6.50, unitPrice: 6.50, casePrice: 39.00, minimumUnitPrice: 5.80, por: 5.00, rrp: 8.50, packSize: '6x750ml', primaryUom: 'Case', orderableUom: 'Case', barcode: '2000000000008', barcodeUom: 'Case', imageId: 208, thumbnailUrl: '', maxQuantity: 30, minQuantity: 1, quantityInUom: 6, internalId: 'VAP-PROSECCO', legacyProductCode: '', caseDimensions: '40x30x30cm', orderCategory: 'Alcohol', productCategory: 'Ambient', gtin: '02000000000008', vatRate: 0.2, weight: 9.5, casesPerPallet: 60, casesPerLayer: 10, minShippingLife: 365, minShelfLife: 730, singlePick: false, caseSize: 6, isFrozen: false },
  { id: 209, sku: 'VAP-APEROL-001', name: 'Aperol 1L', categoryId: 6, category: 'Wines & Spirits', stockOnHand: 4, stockOnOrder: 8, price: 16.50, gbpPrice: 16.50, unitPrice: 16.50, casePrice: 99.00, minimumUnitPrice: 14.50, por: 12.00, rrp: 20.00, packSize: '6x1L', primaryUom: 'Each', orderableUom: 'Case', barcode: '2000000000009', barcodeUom: 'Case', imageId: 209, thumbnailUrl: '', maxQuantity: 20, minQuantity: 1, quantityInUom: 6, internalId: 'VAP-APEROL', legacyProductCode: '', caseDimensions: '35x25x30cm', orderCategory: 'Alcohol', productCategory: 'Ambient', gtin: '02000000000009', vatRate: 0.2, weight: 7.5, casesPerPallet: 80, casesPerLayer: 16, minShippingLife: 730, minShelfLife: 1095, singlePick: false, caseSize: 6, isFrozen: false },
  { id: 210, sku: 'VAP-BASIL-001', name: 'Fresh Basil Plants (Living)', categoryId: 7, category: 'Fresh Produce', stockOnHand: 10, stockOnOrder: 20, price: 2.20, gbpPrice: 2.20, unitPrice: 2.20, casePrice: 26.40, minimumUnitPrice: 1.80, por: 1.50, rrp: 2.80, packSize: '12x1', primaryUom: 'Each', orderableUom: 'Case', barcode: '2000000000010', barcodeUom: 'Case', imageId: 210, thumbnailUrl: '', maxQuantity: 30, minQuantity: 1, quantityInUom: 12, internalId: 'VAP-BASIL', legacyProductCode: '', caseDimensions: '40x30x25cm', orderCategory: 'Fresh Produce', productCategory: 'Fresh', gtin: '02000000000010', vatRate: 0.0, weight: 4.0, casesPerPallet: 80, casesPerLayer: 16, minShippingLife: 5, minShelfLife: 10, singlePick: false, caseSize: 12, isFrozen: false },
  { id: 211, sku: 'VAP-PRAWNS-001', name: 'King Prawns (Raw, Peeled) 1kg', categoryId: 5, category: 'Frozen Seafood', stockOnHand: 4, stockOnOrder: 8, price: 14.50, gbpPrice: 14.50, unitPrice: 14.50, casePrice: 87.00, minimumUnitPrice: 12.50, por: 10.50, rrp: 18.00, packSize: '6x1kg', primaryUom: 'Case', orderableUom: 'Case', barcode: '2000000000011', barcodeUom: 'Case', imageId: 211, thumbnailUrl: '', maxQuantity: 15, minQuantity: 1, quantityInUom: 6, internalId: 'VAP-PRAWNS', legacyProductCode: '', caseDimensions: '40x30x20cm', orderCategory: 'Frozen', productCategory: 'Frozen', gtin: '02000000000011', vatRate: 0.0, weight: 6.5, casesPerPallet: 48, casesPerLayer: 8, minShippingLife: 180, minShelfLife: 365, singlePick: false, caseSize: 6, isFrozen: true },
  { id: 212, sku: 'VAP-PENNE-001', name: 'Penne Rigate (Bronze Die) 500g', categoryId: 4, category: 'Dry Pasta', stockOnHand: 20, stockOnOrder: 40, price: 2.10, gbpPrice: 2.10, unitPrice: 2.10, casePrice: 25.20, minimumUnitPrice: 1.80, por: 1.50, rrp: 2.80, packSize: '12x500g', primaryUom: 'Case', orderableUom: 'Case', barcode: '2000000000012', barcodeUom: 'Case', imageId: 212, thumbnailUrl: '', maxQuantity: 50, minQuantity: 1, quantityInUom: 12, internalId: 'VAP-PENNE', legacyProductCode: '', caseDimensions: '45x30x25cm', orderCategory: 'Dry Goods', productCategory: 'Ambient', gtin: '02000000000012', vatRate: 0.0, weight: 6.5, casesPerPallet: 80, casesPerLayer: 16, minShippingLife: 365, minShelfLife: 730, singlePick: false, caseSize: 12, isFrozen: false },
];

/** Five Guys-specific orderable items */
const fiveGuysItems: Item[] = [
  { id: 301, sku: 'FG-BEEF-001', name: 'Fresh Ground Beef Patties 4oz x48', categoryId: 8, category: 'Fresh Beef', stockOnHand: 8, stockOnOrder: 16, price: 68.00, gbpPrice: 68.00, unitPrice: 68.00, casePrice: 68.00, minimumUnitPrice: 62.00, por: 55.00, rrp: 75.00, packSize: '1x48', primaryUom: 'Case', orderableUom: 'Case', barcode: '3000000000001', barcodeUom: 'Case', imageId: 301, thumbnailUrl: '', maxQuantity: 30, minQuantity: 1, quantityInUom: 1, internalId: 'FG-BEEF', legacyProductCode: '', caseDimensions: '50x40x20cm', orderCategory: 'Fresh Meat', productCategory: 'Fresh', gtin: '03000000000001', vatRate: 0.0, weight: 5.5, casesPerPallet: 48, casesPerLayer: 8, minShippingLife: 3, minShelfLife: 5, singlePick: false, caseSize: 1, isFrozen: false },
  { id: 302, sku: 'FG-POTATO-001', name: 'Fresh Potatoes (Maris Piper) 25kg', categoryId: 7, category: 'Fresh Produce', stockOnHand: 12, stockOnOrder: 24, price: 8.50, gbpPrice: 8.50, unitPrice: 8.50, casePrice: 8.50, minimumUnitPrice: 7.00, por: 6.00, rrp: 10.00, packSize: '1x25kg', primaryUom: 'Bag', orderableUom: 'Bag', barcode: '3000000000002', barcodeUom: 'Bag', imageId: 302, thumbnailUrl: '', maxQuantity: 40, minQuantity: 2, quantityInUom: 1, internalId: 'FG-POTATO', legacyProductCode: '', caseDimensions: '60x40x30cm', orderCategory: 'Fresh Produce', productCategory: 'Fresh', gtin: '03000000000002', vatRate: 0.0, weight: 25.5, casesPerPallet: 24, casesPerLayer: 4, minShippingLife: 7, minShelfLife: 14, singlePick: false, caseSize: 1, isFrozen: false },
  { id: 303, sku: 'FG-PEANUT-OIL-001', name: '100% Refined Peanut Oil 20L', categoryId: 4, category: 'Oils', stockOnHand: 4, stockOnOrder: 8, price: 32.00, gbpPrice: 32.00, unitPrice: 32.00, casePrice: 32.00, minimumUnitPrice: 28.00, por: 24.00, rrp: 38.00, packSize: '1x20L', primaryUom: 'Each', orderableUom: 'Each', barcode: '3000000000003', barcodeUom: 'Each', imageId: 303, thumbnailUrl: '', maxQuantity: 15, minQuantity: 1, quantityInUom: 1, internalId: 'FG-PEANUT-OIL', legacyProductCode: '', caseDimensions: '30x30x45cm', orderCategory: 'Oils', productCategory: 'Ambient', gtin: '03000000000003', vatRate: 0.0, weight: 18.5, casesPerPallet: 30, casesPerLayer: 6, minShippingLife: 180, minShelfLife: 365, singlePick: true, caseSize: 1, isFrozen: false },
  { id: 304, sku: 'FG-BUNS-001', name: 'Sesame Seed Burger Buns x60', categoryId: 2, category: 'Bakery', stockOnHand: 6, stockOnOrder: 12, price: 18.00, gbpPrice: 18.00, unitPrice: 18.00, casePrice: 18.00, minimumUnitPrice: 16.00, por: 13.00, rrp: 22.00, packSize: '1x60', primaryUom: 'Case', orderableUom: 'Case', barcode: '3000000000004', barcodeUom: 'Case', imageId: 304, thumbnailUrl: '', maxQuantity: 20, minQuantity: 1, quantityInUom: 1, internalId: 'FG-BUNS', legacyProductCode: '', caseDimensions: '50x40x30cm', orderCategory: 'Bakery', productCategory: 'Fresh', gtin: '03000000000004', vatRate: 0.0, weight: 4.5, casesPerPallet: 48, casesPerLayer: 8, minShippingLife: 3, minShelfLife: 5, singlePick: false, caseSize: 1, isFrozen: false },
  { id: 305, sku: 'FG-CHEESE-001', name: 'American Cheese Slices x160', categoryId: 1, category: 'Dairy', stockOnHand: 4, stockOnOrder: 8, price: 22.50, gbpPrice: 22.50, unitPrice: 22.50, casePrice: 22.50, minimumUnitPrice: 20.00, por: 17.00, rrp: 26.00, packSize: '1x160', primaryUom: 'Case', orderableUom: 'Case', barcode: '3000000000005', barcodeUom: 'Case', imageId: 305, thumbnailUrl: '', maxQuantity: 15, minQuantity: 1, quantityInUom: 1, internalId: 'FG-CHEESE', legacyProductCode: '', caseDimensions: '40x30x15cm', orderCategory: 'Dairy', productCategory: 'Fresh', gtin: '03000000000005', vatRate: 0.0, weight: 4.0, casesPerPallet: 72, casesPerLayer: 12, minShippingLife: 30, minShelfLife: 60, singlePick: false, caseSize: 1, isFrozen: false },
  { id: 306, sku: 'FG-BACON-001', name: 'Smoked Back Bacon 2.5kg', categoryId: 8, category: 'Fresh Meat', stockOnHand: 5, stockOnOrder: 10, price: 14.50, gbpPrice: 14.50, unitPrice: 14.50, casePrice: 58.00, minimumUnitPrice: 12.50, por: 10.50, rrp: 17.00, packSize: '4x2.5kg', primaryUom: 'Case', orderableUom: 'Case', barcode: '3000000000006', barcodeUom: 'Case', imageId: 306, thumbnailUrl: '', maxQuantity: 20, minQuantity: 1, quantityInUom: 4, internalId: 'FG-BACON', legacyProductCode: '', caseDimensions: '45x30x20cm', orderCategory: 'Fresh Meat', productCategory: 'Fresh', gtin: '03000000000006', vatRate: 0.0, weight: 10.5, casesPerPallet: 48, casesPerLayer: 8, minShippingLife: 7, minShelfLife: 14, singlePick: false, caseSize: 4, isFrozen: false },
  { id: 307, sku: 'FG-HOTDOG-001', name: 'All-Beef Hot Dogs x40', categoryId: 8, category: 'Fresh Meat', stockOnHand: 3, stockOnOrder: 6, price: 24.00, gbpPrice: 24.00, unitPrice: 24.00, casePrice: 24.00, minimumUnitPrice: 21.00, por: 18.00, rrp: 28.00, packSize: '1x40', primaryUom: 'Case', orderableUom: 'Case', barcode: '3000000000007', barcodeUom: 'Case', imageId: 307, thumbnailUrl: '', maxQuantity: 15, minQuantity: 1, quantityInUom: 1, internalId: 'FG-HOTDOG', legacyProductCode: '', caseDimensions: '45x30x15cm', orderCategory: 'Fresh Meat', productCategory: 'Fresh', gtin: '03000000000007', vatRate: 0.0, weight: 5.0, casesPerPallet: 60, casesPerLayer: 10, minShippingLife: 7, minShelfLife: 14, singlePick: false, caseSize: 1, isFrozen: false },
  { id: 308, sku: 'FG-PEANUTS-001', name: 'Roasted Peanuts (In-Shell) 10kg', categoryId: 4, category: 'Snacks', stockOnHand: 4, stockOnOrder: 8, price: 12.00, gbpPrice: 12.00, unitPrice: 12.00, casePrice: 12.00, minimumUnitPrice: 10.00, por: 8.50, rrp: 15.00, packSize: '1x10kg', primaryUom: 'Bag', orderableUom: 'Bag', barcode: '3000000000008', barcodeUom: 'Bag', imageId: 308, thumbnailUrl: '', maxQuantity: 15, minQuantity: 1, quantityInUom: 1, internalId: 'FG-PEANUTS', legacyProductCode: '', caseDimensions: '50x35x25cm', orderCategory: 'Snacks', productCategory: 'Ambient', gtin: '03000000000008', vatRate: 0.0, weight: 10.5, casesPerPallet: 40, casesPerLayer: 8, minShippingLife: 90, minShelfLife: 180, singlePick: false, caseSize: 1, isFrozen: false },
  { id: 309, sku: 'FG-CAJUN-001', name: 'Five Guys Cajun Seasoning 2kg', categoryId: 4, category: 'Seasonings', stockOnHand: 2, stockOnOrder: 6, price: 8.50, gbpPrice: 8.50, unitPrice: 8.50, casePrice: 51.00, minimumUnitPrice: 7.00, por: 6.00, rrp: 10.00, packSize: '6x2kg', primaryUom: 'Each', orderableUom: 'Case', barcode: '3000000000009', barcodeUom: 'Case', imageId: 309, thumbnailUrl: '', maxQuantity: 10, minQuantity: 1, quantityInUom: 6, internalId: 'FG-CAJUN', legacyProductCode: '', caseDimensions: '30x25x20cm', orderCategory: 'Seasonings', productCategory: 'Ambient', gtin: '03000000000009', vatRate: 0.0, weight: 12.5, casesPerPallet: 100, casesPerLayer: 20, minShippingLife: 365, minShelfLife: 730, singlePick: false, caseSize: 6, isFrozen: false },
  { id: 310, sku: 'FG-MILKBASE-001', name: 'Vanilla Milkshake Base Mix 5L', categoryId: 1, category: 'Dairy', stockOnHand: 5, stockOnOrder: 10, price: 9.80, gbpPrice: 9.80, unitPrice: 9.80, casePrice: 58.80, minimumUnitPrice: 8.50, por: 7.00, rrp: 12.00, packSize: '6x5L', primaryUom: 'Case', orderableUom: 'Case', barcode: '3000000000010', barcodeUom: 'Case', imageId: 310, thumbnailUrl: '', maxQuantity: 20, minQuantity: 1, quantityInUom: 6, internalId: 'FG-MILKBASE', legacyProductCode: '', caseDimensions: '45x35x30cm', orderCategory: 'Dairy', productCategory: 'Fresh', gtin: '03000000000010', vatRate: 0.0, weight: 31.0, casesPerPallet: 24, casesPerLayer: 4, minShippingLife: 14, minShelfLife: 28, singlePick: false, caseSize: 6, isFrozen: false },
  { id: 311, sku: 'FG-LETTUCE-001', name: 'Iceberg Lettuce (Whole) x12', categoryId: 7, category: 'Fresh Produce', stockOnHand: 6, stockOnOrder: 12, price: 8.00, gbpPrice: 8.00, unitPrice: 8.00, casePrice: 8.00, minimumUnitPrice: 6.50, por: 5.50, rrp: 9.50, packSize: '1x12', primaryUom: 'Case', orderableUom: 'Case', barcode: '3000000000011', barcodeUom: 'Case', imageId: 311, thumbnailUrl: '', maxQuantity: 15, minQuantity: 1, quantityInUom: 1, internalId: 'FG-LETTUCE', legacyProductCode: '', caseDimensions: '50x40x30cm', orderCategory: 'Fresh Produce', productCategory: 'Fresh', gtin: '03000000000011', vatRate: 0.0, weight: 7.5, casesPerPallet: 36, casesPerLayer: 6, minShippingLife: 3, minShelfLife: 7, singlePick: false, caseSize: 1, isFrozen: false },
  { id: 312, sku: 'FG-TOMATO-001', name: 'Vine Tomatoes 5kg', categoryId: 7, category: 'Fresh Produce', stockOnHand: 4, stockOnOrder: 8, price: 6.50, gbpPrice: 6.50, unitPrice: 6.50, casePrice: 6.50, minimumUnitPrice: 5.50, por: 4.50, rrp: 8.00, packSize: '1x5kg', primaryUom: 'Case', orderableUom: 'Case', barcode: '3000000000012', barcodeUom: 'Case', imageId: 312, thumbnailUrl: '', maxQuantity: 20, minQuantity: 1, quantityInUom: 1, internalId: 'FG-TOMATO', legacyProductCode: '', caseDimensions: '40x30x20cm', orderCategory: 'Fresh Produce', productCategory: 'Fresh', gtin: '03000000000012', vatRate: 0.0, weight: 5.5, casesPerPallet: 60, casesPerLayer: 10, minShippingLife: 5, minShelfLife: 7, singlePick: false, caseSize: 1, isFrozen: false },
];

/** Returns the appropriate item list based on the store number */
function getItemsForStore(storeNumber?: string): Item[] {
  if (!storeNumber) return mockItems;
  if (storeNumber.startsWith('VAP')) return vapianoItems;
  if (storeNumber.startsWith('FG')) return fiveGuysItems;
  return mockItems;
}

/* ---- Helper Functions ---- */

function delay(ms: number = 300): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function success<T>(data: T, message: string = 'Success'): Result<T> {
  return { success: true, message, data };
}

function failure<T>(data: T, message: string = 'An error occurred'): Result<T> {
  return { success: false, message, data };
}

/* ---- Mocked Client Implementation ---- */

export class OrdersClientMocked implements IOrdersClient {
  // Orders CRUD
  async getOrders(options: OrdersFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<Order>>> {
    await delay();

    let filtered = [...mockOrders];

    // Apply filters
    if (options.orderNumber) {
      filtered = filtered.filter((o) => o.id.toString().includes(options.orderNumber!));
    }
    if (options.orderType > 0) {
      filtered = filtered.filter((o) => o.orderTypeId === options.orderType);
    }
    if (options.status > 0) {
      filtered = filtered.filter((o) => o.statusId === options.status);
    }
    if (options.deliveryDateFrom) {
      filtered = filtered.filter((o) => o.deliveryDate >= options.deliveryDateFrom!);
    }
    if (options.deliveryDateTo) {
      filtered = filtered.filter((o) => o.deliveryDate <= options.deliveryDateTo!);
    }

    // Pagination
    const totalCount = filtered.length;
    const pageSize = options.pageSize || 10;
    const pageNumber = options.pageNumber || 1;
    const totalPages = Math.ceil(totalCount / pageSize);
    const start = (pageNumber - 1) * pageSize;
    const items = filtered.slice(start, start + pageSize);

    return success<PaginatedList<Order>>({
      items,
      pageNumber,
      totalPages,
      totalCount,
      hasPreviousPage: pageNumber > 1,
      hasNextPage: pageNumber < totalPages,
      sort: options.sort || 'id',
      direction: options.direction || 'asc',
    });
  }

  async getOrder(storeNumber: string | undefined, orderId: number, cancelToken?: CancelToken): Promise<Result<OrderDetails>> {
    await delay();

    const order = mockOrders.find((o) => o.id === orderId);
    if (!order) {
      return failure(mockOrderDetails, 'Order not found');
    }

    // Return the mock order details with updated id
    return success({ ...mockOrderDetails, id: orderId });
  }

  async getOrderByDeliveryDate(storeNumber: string | undefined, orderTypeId: number, deliveryDate: string, supplierNumber: string, cancelToken?: CancelToken): Promise<Result<OrderDetails>> {
    await delay();

    const order = mockOrders.find(
      (o) => o.orderTypeId === orderTypeId && o.deliveryDate === deliveryDate,
    );

    if (order) {
      return success({ ...mockOrderDetails, id: order.id, deliveryDate, orderTypeId });
    }

    return failure(mockOrderDetails, 'Order not found');
  }

  async createOrder(request: CreateOrderRequest, cancelToken?: CancelToken): Promise<Result<number>> {
    await delay();

    const newId = nextOrderId++;
    const newOrder: Order = {
      id: newId,
      statusId: OrderStatusId.Draft,
      status: 'Draft',
      emergencyOrder: request.emergencyOrder,
      submitted: false,
      orderTypeId: request.orderTypeId,
      orderType: mockOrderTypes.find((ot) => ot.id === request.orderTypeId)?.name || 'Unknown',
      deliveryDate: request.deliveryDate,
      cutOffDate: request.cutOffDate,
      cutOffTime: '18:00',
      totalCost: 0,
      totalQuantity: request.items.reduce((sum, item) => sum + item.quantity, 0),
      supplierName: mockSuppliers.find((s) => s.number === request.supplierNumber)?.name || 'Unknown',
      externallyCreatedOrder: false,
      deliveryDateUpdated: false,
      isAutoship: false,
    };

    mockOrders.push(newOrder);
    return success(newId, 'Order created successfully');
  }

  async updateOrder(storeNumber: string | undefined, request: OrderUpdate, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();

    const orderIndex = mockOrders.findIndex((o) => o.id === request.id);
    if (orderIndex === -1) {
      return failure(false, 'Order not found');
    }

    // Update quantities in mock
    const totalQuantity = request.items.reduce((sum, item) => sum + item.quantity, 0);
    mockOrders[orderIndex].totalQuantity = totalQuantity;

    return success(true, 'Order updated successfully');
  }

  async deleteOrder(storeNumber: string | undefined, orderId: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();

    const orderIndex = mockOrders.findIndex((o) => o.id.toString() === orderId);
    if (orderIndex === -1) {
      return failure(false, 'Order not found');
    }

    mockOrders.splice(orderIndex, 1);
    return success(true, 'Order deleted successfully');
  }

  async duplicateOrder(storeNumber: string | undefined, sourceOrderId: number, cancelToken?: CancelToken): Promise<Result<number>> {
    await delay();

    const sourceOrder = mockOrders.find((o) => o.id === sourceOrderId);
    if (!sourceOrder) {
      return failure(0, 'Source order not found');
    }

    const newId = nextOrderId++;
    const duplicatedOrder: Order = {
      ...sourceOrder,
      id: newId,
      statusId: OrderStatusId.Draft,
      status: 'Draft',
      submitted: false,
    };

    mockOrders.push(duplicatedOrder);
    return success(newId, 'Order duplicated successfully');
  }

  async copyOrder(storeNumber: string | undefined, orderId: string | undefined, copyOrderId: string | undefined, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true, 'Order copied successfully');
  }

  // Order Items
  async addOrderItem(storeNumber: string | undefined, request: AddOrderItemRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true, 'Item added successfully');
  }

  async deleteOrderItem(storeNumber: string | undefined, orderId: string, orderItemId: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true, 'Item deleted successfully');
  }

  async deleteOrderItems(storeNumber: string | undefined, request: DeleteOrderItemsRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true, 'Items deleted successfully');
  }

  async deleteAllOrderItems(storeNumber: string | undefined, orderId: string | undefined, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true, 'All items deleted successfully');
  }

  // Order Submission
  async checkoutOrder(storeNumber: string | undefined, orderId: string | undefined, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true, 'Order checked out successfully');
  }

  async submitOrder(storeNumber: string | undefined, orderId: string, orderDetails: OrderDetails, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();

    const orderIndex = mockOrders.findIndex((o) => o.id.toString() === orderId);
    if (orderIndex !== -1) {
      mockOrders[orderIndex].statusId = OrderStatusId.Submitted;
      mockOrders[orderIndex].status = 'Submitted';
      mockOrders[orderIndex].submitted = true;
    }

    return success(true, 'Order submitted successfully');
  }

  // Order Types & Schedules
  async getOrderTypes(storeNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<OrderType[]>> {
    await delay();
    return success(mockOrderTypes);
  }

  async getDeliverySchedules(storeNumber: string | undefined, orderType: number | undefined, supplierNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<DeliverySchedule[]>> {
    await delay();

    let filtered = [...mockDeliverySchedules];
    if (orderType) {
      filtered = filtered.filter((ds) => ds.orderType === orderType);
    }
    if (supplierNumber) {
      filtered = filtered.filter((ds) => ds.supplier === supplierNumber);
    }

    return success(filtered);
  }

  async getSuppliers(storeNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<Supplier[]>> {
    await delay();
    return success(mockSuppliers);
  }

  async getCategories(_storeNumber: string | undefined, _orderType: number, _supplier: string | undefined, _cancelToken?: CancelToken): Promise<Result<Category[]>> {
    await delay();

    const mockCategories: Category[] = [
      {
        id: 1,
        name: 'Dairy',
        display: true,
        showInFilters: true,
        showInNavigation: true,
        priority: 1,
        fullCategoryString: 'Dairy',
        parentId: null,
        parentName: '',
        parentFullCategoryString: '',
      },
      {
        id: 2,
        name: 'Bakery',
        display: true,
        showInFilters: true,
        showInNavigation: true,
        priority: 2,
        fullCategoryString: 'Bakery',
        parentId: null,
        parentName: '',
        parentFullCategoryString: '',
      },
      {
        id: 3,
        name: 'Coffee',
        display: true,
        showInFilters: true,
        showInNavigation: true,
        priority: 3,
        fullCategoryString: 'Coffee',
        parentId: null,
        parentName: '',
        parentFullCategoryString: '',
      },
      {
        id: 4,
        name: 'Dry Goods',
        display: true,
        showInFilters: true,
        showInNavigation: true,
        priority: 4,
        fullCategoryString: 'Dry Goods',
        parentId: null,
        parentName: '',
        parentFullCategoryString: '',
      },
      {
        id: 5,
        name: 'Frozen',
        display: true,
        showInFilters: true,
        showInNavigation: true,
        priority: 5,
        fullCategoryString: 'Frozen',
        parentId: null,
        parentName: '',
        parentFullCategoryString: '',
      },
    ];

    return success(mockCategories);
  }

  // Order Form
  async getOrderForm(_storeNumber: string | undefined, orderTypeId: number, categoryId: number, _sort: string, _direction: string, itemSearch: string, _supplierNumber: string, _cancelToken?: CancelToken): Promise<Result<OrderForm>> {
    await delay();

    // Get relevant delivery schedules for this order type
    const relevantSchedules = mockDeliverySchedules.filter(ds => ds.orderType === orderTypeId);
    const availableDates = relevantSchedules.slice(0, 4).map(ds => ({
      deliveryDate: ds.deliveryDate,
      cutOffTime: ds.cutOffTime,
    }));

    // Filter items based on store, search, and category
    let filteredItems = [...getItemsForStore(_storeNumber ?? undefined)];
    
    if (itemSearch) {
      const searchTerm = itemSearch.toLowerCase();
      filteredItems = filteredItems.filter(item =>
        item.name.toLowerCase().includes(searchTerm) ||
        item.sku.toLowerCase().includes(searchTerm),
      );
    }

    if (categoryId > 0) {
      filteredItems = filteredItems.filter(item => item.categoryId === categoryId);
    }

    // Group items by category
    const categoriesMap = new Map<number, { category: Category; items: typeof filteredItems }>();
    
    filteredItems.forEach(item => {
      if (!categoriesMap.has(item.categoryId)) {
        categoriesMap.set(item.categoryId, {
          category: {
            id: item.categoryId,
            name: item.category,
            display: true,
            showInFilters: true,
            showInNavigation: true,
            priority: item.categoryId,
            fullCategoryString: item.category,
            parentId: null,
            parentName: '',
            parentFullCategoryString: '',
          },
          items: [],
        });
      }
      categoriesMap.get(item.categoryId)!.items.push(item);
    });

    // Build order form categories with items
    const orderFormCategories = Array.from(categoriesMap.values()).map(({ category, items }) => ({
      ...category,
      items: items.map(item => ({
        ...item,
        quantity: new Array(availableDates.length).fill(0), // Initialize with 0 for each delivery date
        lastTimeSold: '10-12-2025',
      })),
    }));

    const mockOrderForm: OrderForm = {
      categories: orderFormCategories,
      availableDates: availableDates,
      isOrderTypeAutoship: false,
    };

    return success(mockOrderForm);
  }

  async saveOrderForm(storeNumber: string | undefined, request: OrderFormUpdate, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true, 'Order form saved successfully');
  }

  async downloadOrderForm(storeNumber: string | undefined, orderTypeId: number, supplierNumber: string, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    await delay();
    return success({ fileData: 'base64data...', fileName: 'order_form.xlsx' });
  }

  async uploadOrderForm(storeNumber: string | undefined, orderTypeId: number, supplierNumber: string, request: FileRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true, 'Order form uploaded successfully');
  }

  // Order Spreadsheet
  async downloadOrderSpreadsheet(storeNumber: string | undefined, orderId: string, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    await delay();
    return success({ fileData: 'base64data...', fileName: `order_${orderId}.xlsx` });
  }

  async uploadOrderSpreadsheet(storeNumber: string | undefined, orderId: string, request: FileRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true, 'Order spreadsheet uploaded successfully');
  }

  // Shipping
  async getShippingDetails(storeNumber: string, orderId: string, shippingMethodId?: string, cancelToken?: CancelToken): Promise<Result<ShippingDetailsRequest>> {
    await delay();

    return success({
      deliveryMethod: 'Standard',
      deliveryMethodId: '1',
      orderStatus: 'Draft',
      deliveryCharge: 15.00,
      isDisabled: false,
      country: 'UK',
      location: 'London',
      containerType: 1,
      palletType: 1,
      deliveryOptions: [
        { name: 'Standard', shippingMethodId: '1', charge: 15.00 },
        { name: 'Express', shippingMethodId: '2', charge: 25.00 },
      ],
    });
  }

  async setShippingDetails(storeNumber: string, orderId: string, shippingDetails: ShippingDetailsRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true, 'Shipping details set successfully');
  }

  async getStoreContainerData(storeNumber: string | undefined, orderNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<ContainerFillResult>> {
    await delay();

    return success({
      creditLimit: 10000,
      minFill: 70,
      containerType: '40ft',
      palletType: 'Euro',
      currentFill: 85,
      orderWeight: 1250,
      maxPalletsPerContainer: 24,
      totalPalletsNeeded: 20,
      totalContainersUsed: 1,
      containerFillPercents: [85],
      errors: [],
    });
  }

  // Audit
  async getOrderAudit(storeNumber: string | undefined, orderNumber: number, cancelToken?: CancelToken): Promise<Result<(string | undefined | null)[]>> {
    await delay();

    return success([
      '2025-12-10 09:00:00 - Order created by John Smith',
      '2025-12-10 10:30:00 - Item SKU-MILK-001 added (qty: 4)',
      '2025-12-10 10:35:00 - Item SKU-BREAD-001 added (qty: 10)',
      '2025-12-11 14:00:00 - Quantity updated for SKU-MILK-001 (4 -> 6)',
    ]);
  }

  // Order Approval
  async approveOrder(storeNumber: string | undefined, orderId: string | undefined, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();

    const orderIndex = mockOrders.findIndex((o) => o.id.toString() === orderId);
    if (orderIndex !== -1) {
      mockOrders[orderIndex].statusId = OrderStatusId.Confirmed;
      mockOrders[orderIndex].status = 'Confirmed';
    }

    return success(true, 'Order approved successfully');
  }

  async approveAmendment(storeNumber: string | undefined, orderId: string | undefined, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true, 'Amendment approved successfully');
  }

  async rejectOrder(storeNumber: string | undefined, orderId: string | undefined, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();

    const orderIndex = mockOrders.findIndex((o) => o.id.toString() === orderId);
    if (orderIndex !== -1) {
      mockOrders[orderIndex].statusId = OrderStatusId.Cancelled;
      mockOrders[orderIndex].status = 'Cancelled';
    }

    return success(true, 'Order rejected successfully');
  }

  async rejectAmendment(storeNumber: string | undefined, orderId: string | undefined, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true, 'Amendment rejected successfully');
  }

  // Order Delivery Date
  async updateOrderDeliveryDate(storeNumber: string | undefined, orderId: string, deliveryDate: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();

    const orderIndex = mockOrders.findIndex((o) => o.id.toString() === orderId);
    if (orderIndex !== -1) {
      mockOrders[orderIndex].deliveryDate = deliveryDate;
      mockOrders[orderIndex].deliveryDateUpdated = true;
    }

    return success(true, 'Delivery date updated successfully');
  }

  // Items
  async getItems(options: ItemsFilterOptions, _cancelToken?: CancelToken): Promise<Result<PaginatedList<Item>>> {
    await delay();
    
    let filtered = [...getItemsForStore(options.storeNumber ?? undefined)];

    // Apply filters
    if (options.item) {
      const searchTerm = options.item.toLowerCase();
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(searchTerm) ||
        item.sku.toLowerCase().includes(searchTerm),
      );
    }

    if (options.categoryId && options.categoryId > 0) {
      filtered = filtered.filter(item => item.categoryId === options.categoryId);
    }

    if (options.orderType && options.orderType > 0) {
      // Filter by order type if needed - for now return all items
      // In a real implementation, items would be filtered by supplier/order type
    }

    if (options.supplier) {
      // Filter by supplier if needed - for now return all items
    }

    // Sort
    if (options.sort) {
      const sortField = options.sort.toLowerCase();
      const direction = options.direction?.toLowerCase() === 'desc' ? -1 : 1;
      
      filtered.sort((a, b) => {
        let aVal: string | number;
        let bVal: string | number;
        
        switch (sortField) {
          case 'name':
            aVal = a.name.toLowerCase();
            bVal = b.name.toLowerCase();
            break;
          case 'price':
            aVal = a.price;
            bVal = b.price;
            break;
          case 'category':
            aVal = a.category.toLowerCase();
            bVal = b.category.toLowerCase();
            break;
          default:
            aVal = a.name.toLowerCase();
            bVal = b.name.toLowerCase();
        }
        
        if (aVal < bVal) return -1 * direction;
        if (aVal > bVal) return 1 * direction;
        return 0;
      });
    }

    // Pagination
    const pageNumber = options.pageNumber || 0;
    const pageSize = options.pageSize || 20;
    const startIndex = pageNumber * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedItems = filtered.slice(startIndex, endIndex);
    const totalCount = filtered.length;
    const totalPages = Math.ceil(totalCount / pageSize);

    return success({
      items: paginatedItems,
      pageNumber: pageNumber,
      totalPages: totalPages,
      totalCount: totalCount,
      hasPreviousPage: pageNumber > 0,
      hasNextPage: pageNumber < totalPages - 1,
      sort: options.sort ?? '',
      direction: options.direction ?? '',
    });
  }

  async getItemsByFranchiseName(options: ItemFilterParams, cancelToken?: CancelToken): Promise<Result<PaginatedList<Item>>> {
    await delay();
    return success({
      items: [],
      pageNumber: options.pageNumber,
      totalPages: 0,
      totalCount: 0,
      hasPreviousPage: false,
      hasNextPage: false,
      sort: '',
      direction: '',
    });
  }

  async getItem(storeNumber: string | undefined, itemId: number, cancelToken?: CancelToken): Promise<Result<Item>> {
    await delay();
    return success({} as Item);
  }

  async getItemByBarcode(storeNumber: string | undefined, barcode: string, stockCount: string | undefined, user: string | undefined, cancelToken?: CancelToken): Promise<Result<Item>> {
    await delay();
    return success({} as Item);
  }

  async getItemBySku(storeNumber: string | undefined, sku: string, forOfd?: boolean, cancelToken?: CancelToken): Promise<Result<Item>> {
    await delay();
    console.log('[ordersClientMocked] getItemBySku called with SKU:', sku, 'for store:', storeNumber);
    const allItems = [...mockItems, ...vapianoItems, ...fiveGuysItems];
    const item = allItems.find(i => i.sku === sku);
    console.log('[ordersClientMocked] Found item:', item?.name, 'with SKU:', item?.sku);
    if (!item) {
      console.log('[ordersClientMocked] Item not found! Available SKUs:', allItems.map(i => i.sku));
      return failure({} as Item, `Item with SKU ${sku} not found`);
    }
    return success(item);
  }

  async getItemDocuments(storeNumber: string | undefined, sku: string, cancelToken?: CancelToken): Promise<Result<ItemDocument[]>> {
    await delay();
    
    // Return mock documents based on SKU
    const allItems = [...mockItems, ...vapianoItems, ...fiveGuysItems];
    const item = allItems.find(i => i.sku === sku);
    if (!item) return success([]);
    
    // Create mock documents for the item
    const mockDocuments: ItemDocument[] = [
      {
        Id: item.id * 10 + 1,
        ProductId: item.id,
        DocumentId: item.id * 100 + 1,
        DocumentName: 'Product Specification Sheet',
        EffectiveStartDate: '01/01/2024 00:00:00',
        EffectiveEndDate: '',
        LastUpdated: '15/01/2025 10:30:00',
        DocumentType: 'Specification',
      },
      {
        Id: item.id * 10 + 2,
        ProductId: item.id,
        DocumentId: item.id * 100 + 2,
        DocumentName: 'Safety Data Sheet',
        EffectiveStartDate: '01/01/2024 00:00:00',
        EffectiveEndDate: '',
        LastUpdated: '10/01/2025 14:20:00',
        DocumentType: 'Safety',
      },
    ];
    
    return success(mockDocuments);
  }

  async getOrderDocuments(storeNumber: string | undefined, orderId: number, cancelToken?: CancelToken): Promise<Result<{ [itemId: number]: ItemDocument[] }>> {
    await delay();
    
    // Return mock documents for order items
    // Note: formatDate expects dates in DD/MM/YYYY HH:MM:SS format
    const mockDocuments: { [itemId: number]: ItemDocument[] } = {
      101: [ // Whole Milk 2L
        {
          Id: 1001,
          ProductId: 101,
          DocumentId: 5001,
          DocumentName: 'Product Specification Sheet',
          EffectiveStartDate: '01/01/2024 00:00:00',
          EffectiveEndDate: '',
          LastUpdated: '15/01/2025 10:30:00',
          DocumentType: 'Specification',
        },
        {
          Id: 1002,
          ProductId: 101,
          DocumentId: 5002,
          DocumentName: 'Allergen Information',
          EffectiveStartDate: '01/01/2024 00:00:00',
          EffectiveEndDate: '',
          LastUpdated: '10/01/2025 14:20:00',
          DocumentType: 'Safety',
        },
      ],
      103: [ // 1kg Arabica Coffee Beans
        {
          Id: 1003,
          ProductId: 103,
          DocumentId: 5003,
          DocumentName: 'Fair Trade Certificate',
          EffectiveStartDate: '01/06/2024 00:00:00',
          EffectiveEndDate: '31/05/2025 23:59:59',
          LastUpdated: '01/06/2024 08:00:00',
          DocumentType: 'Certificate',
        },
        {
          Id: 1004,
          ProductId: 103,
          DocumentId: 5004,
          DocumentName: 'Roasting Profile',
          EffectiveStartDate: '01/01/2024 00:00:00',
          EffectiveEndDate: '',
          LastUpdated: '05/12/2024 11:45:00',
          DocumentType: 'Technical',
        },
      ],
      104: [ // Tomato Sauce 500g
        {
          Id: 1005,
          ProductId: 104,
          DocumentId: 5005,
          DocumentName: 'Nutritional Information',
          EffectiveStartDate: '01/01/2024 00:00:00',
          EffectiveEndDate: '',
          LastUpdated: '05/01/2025 09:15:00',
          DocumentType: 'Nutritional',
        },
      ],
    };
    
    return success(mockDocuments);
  }

  async getItemAttributes(storeNumber: string | undefined, sku: string, cancelToken?: CancelToken): Promise<Result<ProductAttributeModel[]>> {
    await delay();
    
    // Return mock export attributes for products
    const mockAttributes: ProductAttributeModel[] = [
      {
        attributeDefinitionId: 1,
        fieldName: 'Country of Origin',
        value: 'Ireland',
        dataType: 'text',
        isOptional: false,
        displayOrder: 1,
        isMarketSpecific: true,
        isMultilineText: false,
      },
      {
        attributeDefinitionId: 2,
        fieldName: 'HS Code',
        value: '0401.10.10',
        dataType: 'text',
        mask: '####.##.##',
        isOptional: false,
        displayOrder: 2,
        isMarketSpecific: true,
        isMultilineText: false,
      },
      {
        attributeDefinitionId: 3,
        fieldName: 'Customs Value',
        value: '2.50',
        dataType: 'decimal',
        isOptional: false,
        displayOrder: 3,
        isMarketSpecific: true,
        isMultilineText: false,
      },
      {
        attributeDefinitionId: 4,
        fieldName: 'Product Description (Export)',
        value: 'Fresh dairy product for consumption',
        dataType: 'text',
        isOptional: true,
        displayOrder: 4,
        isMarketSpecific: true,
        isMultilineText: true,
      },
      {
        attributeDefinitionId: 5,
        fieldName: 'Manufacturer',
        value: 'Local Dairy Co Ltd',
        dataType: 'text',
        isOptional: true,
        displayOrder: 5,
        isMarketSpecific: false,
        isMultilineText: false,
      },
    ];
    
    return success(mockAttributes);
  }

  async updateItemAttributes(storeNumber: string | undefined, request: ProductAttributesRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true);
  }

  // Catalogues
  async getCatalogues(options: CatalogueFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<Catalogue>>> {
    await delay();
    
    let filteredCatalogues = [...mockCatalogues];
    
    // Filter by name if provided
    if (options.name) {
      const searchTerm = options.name.toLowerCase();
      filteredCatalogues = filteredCatalogues.filter(c => 
        c.name.toLowerCase().includes(searchTerm) || 
        (c.orderType && c.orderType.toLowerCase().includes(searchTerm)) ||
        (c.market && c.market.toLowerCase().includes(searchTerm)),
      );
    }
    
    // Filter by storeNumber if provided (all catalogues available for now)
    
    // Apply sorting
    if (options.sort) {
      const sortField = options.sort as keyof Catalogue;
      const direction = options.direction === 'asc' ? 1 : -1;
      filteredCatalogues.sort((a, b) => {
        const aVal = a[sortField];
        const bVal = b[sortField];
        if (aVal === undefined || bVal === undefined) return 0;
        if (aVal < bVal) return -1 * direction;
        if (aVal > bVal) return 1 * direction;
        return 0;
      });
    }
    
    // Apply pagination
    const pageSize = options.pageSize || 10;
    const pageNumber = options.pageNumber || 0;
    const start = pageNumber * pageSize;
    const end = start + pageSize;
    const paginatedItems = filteredCatalogues.slice(start, end);
    const totalCount = filteredCatalogues.length;
    const totalPages = Math.ceil(totalCount / pageSize);
    
    return success({
      items: paginatedItems,
      pageNumber: pageNumber,
      totalPages: totalPages,
      totalCount: totalCount,
      hasPreviousPage: pageNumber > 0,
      hasNextPage: pageNumber < totalPages - 1,
      sort: options.sort ?? '',
      direction: options.direction ?? '',
    });
  }

  async getCatalogueRetailers(catalogueId: string, storeNumber: string, cancelToken?: CancelToken): Promise<Result<string[]>> {
    await delay();
    return success([]);
  }

  async getCatalogueProducts(catalogueId: string, options: CatalogueProductsFilterOptions, cancelToken?: CancelToken): Promise<Result<CatalogueProducts>> {
    await delay();
    
    // Get the catalogue name
    const catalogue = mockCatalogues.find(c => c.id.toString() === catalogueId);
    const catalogueName = catalogue?.name || 'Unknown Catalogue';
    
    // Start with all items
    let filteredItems = [...mockItems];
    
    // Filter by search term
    if (options.searchTerm) {
      const searchLower = options.searchTerm.toLowerCase();
      filteredItems = filteredItems.filter(item => 
        item.name.toLowerCase().includes(searchLower) ||
        item.sku.toLowerCase().includes(searchLower),
      );
    }
    
    // Filter by category
    if (options.category) {
      const categoryIds = options.category.split(',').map(c => c.trim()).filter(c => c);
      if (categoryIds.length > 0) {
        filteredItems = filteredItems.filter(item => 
          categoryIds.includes(item.category) || categoryIds.includes(item.categoryId.toString()),
        );
      }
    }
    
    // Apply sorting
    if (options.sort) {
      const sortField = options.sort as keyof Item;
      const direction = options.direction === 'asc' ? 1 : -1;
      filteredItems.sort((a, b) => {
        const aVal = a[sortField];
        const bVal = b[sortField];
        if (aVal === undefined || bVal === undefined) return 0;
        if (aVal < bVal) return -1 * direction;
        if (aVal > bVal) return 1 * direction;
        return 0;
      });
    }
    
    // Apply pagination
    const pageSize = options.pageSize || 10;
    const pageNumber = options.pageNumber || 0;
    const start = pageNumber * pageSize;
    const end = start + pageSize;
    const paginatedItems = filteredItems.slice(start, end);
    const totalCount = filteredItems.length;
    const totalPages = Math.ceil(totalCount / pageSize);
    
    return success({
      catalogueName: catalogueName,
      items: {
        items: paginatedItems,
        pageNumber: pageNumber,
        totalPages: totalPages,
        totalCount: totalCount,
        hasPreviousPage: pageNumber > 0,
        hasNextPage: pageNumber < totalPages - 1,
        sort: options.sort ?? '',
        direction: options.direction ?? '',
      },
    });
  }

  async getCategoriesByCatalogue(storeNumber: string, catalogueId: string, cancelToken?: CancelToken): Promise<Result<Category[]>> {
    await delay();
    // Return all categories for any catalogue
    return success([...mockCategories]);
  }

  async downloadCatalogueExportAttributes(storeNumber: string | undefined, catalogueId: string, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    await delay();
    return success({ fileData: '', fileName: '' });
  }

  async uploadCatalogueExportAttributes(storeNumber: string | undefined, catalogueId: string, request: FileRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true);
  }

  // Currency (stub implementations)
  async getExchangeRates(storeNumber: string | undefined, pageNumber: number, pageSize: number, active: boolean, cancelToken?: CancelToken): Promise<Result<PaginatedList<CurrencyConversionContent>>> {
    await delay();
    return success({
      items: [],
      pageNumber,
      totalPages: 0,
      totalCount: 0,
      hasPreviousPage: false,
      hasNextPage: false,
      sort: '',
      direction: '',
    });
  }

  async updateExchangeRate(storeNumber: string | undefined, request: ExchangeRateRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true);
  }

  async deleteExchangeRate(storeNumber: string | undefined, id: number, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true);
  }

  // News & Alerts (stub implementations)
  async getNews(storeNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<News[]>> {
    await delay();
    return success([]);
  }

  async getOutOfStockAlert(storeNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<OutOfStockUrgentAction>> {
    await delay();
    return success({ hasAlert: false, alertMessage: '', context: '' });
  }

  async getUrgentAlerts(storeNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<OutOfStockUrgentAction>> {
    await delay();
    return success({ hasAlert: false, alertMessage: '', context: '' });
  }

  // Settings (stub implementations)
  async getDefaultOrderSettings(storeNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<DefaultOrderSettings>> {
    await delay();
    return success({ orderTypeId: 1, supplierId: 1, orderType: 'Fresh & Daily', supplier: 'Fresh Foods Ltd', defaultDeliveryDateOffsetDays: 2, 
      defaultCutOffTime: '18:00', autoSelectDeliveryDate: true, isAmbientEnabled: true, isFreshAndDairyEnabled: true, isFrozenEnabled: true, isDsdEnabled: true, 
      isStarbucksStore: false, isWarningEmailEnabled: false , dsdSuppliers: [] });
  }

  async updateDefaultOrderSettings(storeNumber: string | undefined, request: DefaultOrderSettings, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true);
  }

  async getOnboardingDeliveryMethods(storeNumber?: string, cancelToken?: CancelToken): Promise<Result<DeliveryMethodListModel>> {
    await delay();
    return success({ deliveryMethods: [], ShippingMethodList: [] });
  }

  // Instances (stub implementations)
  async getInstance(instanceId: string, cancelToken?: CancelToken): Promise<Result<Instance>> {
    await delay();
    return success({} as Instance);
  }

  async getInstances(options: InstanceFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<Instance>>> {
    await delay();
    
    const mockInstances: Instance[] = [
      {
        id: 1,
        name: 'Chick-fil-A',
        userName: 'cfa-admin',
        domainName: 'cfa.orderly.ie',
        storeSslRoot: 'https://cfa.orderly.ie',
        apiKey: 'mock-api-key-1',
        configuration: {
          uomGroup: 'Imperial',
          brand: 'Chick-fil-A',
          sendGridEmailTemplate: 'default',
          system: OrderlySystem.OFS,
        },
      },
      {
        id: 2,
        name: 'Orderly Demo',
        userName: 'demo-admin',
        domainName: 'demo.orderly.ie',
        storeSslRoot: 'https://demo.orderly.ie',
        apiKey: 'mock-api-key-2',
        configuration: {
          uomGroup: 'Metric',
          brand: 'Orderly',
          sendGridEmailTemplate: 'default',
          system: OrderlySystem.OFS,
        },
      },
      {
        id: 3,
        name: 'Retail Group',
        userName: 'retail-admin',
        domainName: 'retail.orderly.ie',
        storeSslRoot: 'https://retail.orderly.ie',
        apiKey: 'mock-api-key-3',
        configuration: {
          uomGroup: 'Metric',
          brand: 'Retail',
          sendGridEmailTemplate: 'default',
          system: OrderlySystem.OFS,
        },
      },
    ];
    
    let filtered = [...mockInstances];
    if (options.name) {
      filtered = filtered.filter(i => i.name?.toLowerCase().includes(options.name!.toLowerCase()));
    }
    
    const start = (options.pageNumber ?? 0) * (options.pageSize ?? 10);
    const end = start + (options.pageSize ?? 10);
    const items = filtered.slice(start, end);
    
    return success({
      items,
      pageNumber: options.pageNumber ?? 0,
      totalPages: Math.ceil(filtered.length / (options.pageSize ?? 10)),
      totalCount: filtered.length,
      hasPreviousPage: (options.pageNumber ?? 0) > 0,
      hasNextPage: end < filtered.length,
      sort: options.sort ?? '',
      direction: options.direction ?? '',
    });
  }

  async createInstance(request: Instance, cancelToken?: CancelToken): Promise<Result<number>> {
    await delay();
    return success(1);
  }

  async updateInstance(instanceId: string, request: Instance, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true);
  }

  // Markets (stub implementations)
  async getMarket(marketId: string, cancelToken?: CancelToken): Promise<Result<Market>> {
    await delay();
    return success({} as Market);
  }

  async getMarkets(options: MarketFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<Market>>> {
    await delay();
    
    const mockMarkets: Market[] = [
      {
        customerTypeId: 1,
        instanceName: 'Chick-fil-A',
        companyId: 1,
        name: 'Ireland',
        timezone: 'Europe/Dublin',
        currencyCode: 'EUR',
      },
      {
        customerTypeId: 2,
        instanceName: 'Chick-fil-A',
        companyId: 1,
        name: 'United Kingdom',
        timezone: 'Europe/London',
        currencyCode: 'GBP',
      },
      {
        customerTypeId: 3,
        instanceName: 'Orderly Demo',
        companyId: 2,
        name: 'Germany',
        timezone: 'Europe/Berlin',
        currencyCode: 'EUR',
      },
      {
        customerTypeId: 4,
        instanceName: 'Orderly Demo',
        companyId: 2,
        name: 'France',
        timezone: 'Europe/Paris',
        currencyCode: 'EUR',
      },
    ];
    
    let filtered = [...mockMarkets];
    if (options.companyId) {
      filtered = filtered.filter(m => m.companyId === parseInt(options.companyId!));
    }
    if (options.name) {
      filtered = filtered.filter(m => m.name?.toLowerCase().includes(options.name!.toLowerCase()));
    }
    
    const start = (options.pageNumber ?? 0) * (options.pageSize ?? 10);
    const end = start + (options.pageSize ?? 10);
    const items = filtered.slice(start, end);
    
    return success({
      items,
      pageNumber: options.pageNumber ?? 0,
      totalPages: Math.ceil(filtered.length / (options.pageSize ?? 10)),
      totalCount: filtered.length,
      hasPreviousPage: (options.pageNumber ?? 0) > 0,
      hasNextPage: end < filtered.length,
      sort: options.sort ?? '',
      direction: options.direction ?? '',
    });
  }

  async createMarket(request: Market, cancelToken?: CancelToken): Promise<Result<number>> {
    await delay();
    return success(1);
  }

  async updateMarket(marketId: string, request: Market, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true);
  }

  // Market Order Types (stub implementations)
  async getMarketOrderType(marketOrderTypeId: string, cancelToken?: CancelToken): Promise<Result<MarketOrderType>> {
    await delay();
    return success({} as MarketOrderType);
  }

  async getMarketOrderTypes(options: MarketOrderTypeFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<MarketOrderType>>> {
    await delay();
    
    const mockOrderTypes: MarketOrderType[] = [
      {
        companyOrderTypeId: 1,
        instanceName: 'Chick-fil-A',
        companyId: 1,
        orderTypeId: 1,
        name: 'Fresh & Daily',
        marketName: 'Ireland',
        customerTypeId: 1,
        internal: true,
        external: true,
        code: 'FRD',
        cutoff: {
          supplierName: 'Fresh Foods Ltd',
          supplierEmailAddress: 'orders@freshfoods.ie',
          supplierEmailAddress2: '',
          orderCutOffTime: '14:00',
        },
      },
      {
        companyOrderTypeId: 2,
        instanceName: 'Chick-fil-A',
        companyId: 1,
        orderTypeId: 2,
        name: 'Frozen',
        marketName: 'Ireland',
        customerTypeId: 1,
        internal: true,
        external: true,
        code: 'FRZ',
        cutoff: {
          supplierName: 'Ice Cold Distributors',
          supplierEmailAddress: 'orders@icecold.ie',
          supplierEmailAddress2: '',
          orderCutOffTime: '12:00',
        },
      },
      {
        companyOrderTypeId: 3,
        instanceName: 'Chick-fil-A',
        companyId: 1,
        orderTypeId: 3,
        name: 'Ambient',
        marketName: 'Ireland',
        customerTypeId: 1,
        internal: true,
        external: true,
        code: 'AMB',
        cutoff: {
          supplierName: 'Ambient Supplies Co',
          supplierEmailAddress: 'orders@ambient.ie',
          supplierEmailAddress2: '',
          orderCutOffTime: '16:00',
        },
      },
      {
        companyOrderTypeId: 4,
        instanceName: 'Orderly Demo',
        companyId: 2,
        orderTypeId: 4,
        name: 'Daily Delivery',
        marketName: 'Germany',
        customerTypeId: 3,
        internal: true,
        external: true,
        code: 'DAI',
        cutoff: {
          supplierName: 'Demo Supplier GmbH',
          supplierEmailAddress: 'orders@demo.de',
          supplierEmailAddress2: '',
          orderCutOffTime: '15:00',
        },
      },
    ];
    
    let filtered = [...mockOrderTypes];
    if (options.companyId) {
      filtered = filtered.filter(ot => ot.companyId === parseInt(options.companyId!));
    }
    if (options.customerTypeId) {
      filtered = filtered.filter(ot => ot.customerTypeId === parseInt(options.customerTypeId!));
    }
    
    const start = (options.pageNumber ?? 0) * (options.pageSize ?? 10);
    const end = start + (options.pageSize ?? 10);
    const items = filtered.slice(start, end);
    
    return success({
      items,
      pageNumber: options.pageNumber ?? 0,
      totalPages: Math.ceil(filtered.length / (options.pageSize ?? 10)),
      totalCount: filtered.length,
      hasPreviousPage: (options.pageNumber ?? 0) > 0,
      hasNextPage: end < filtered.length,
      sort: options.sort ?? '',
      direction: options.direction ?? '',
    });
  }

  async createMarketOrderType(request: MarketOrderType, cancelToken?: CancelToken): Promise<Result<number>> {
    await delay();
    return success(1);
  }

  async updateMarketOrderType(marketOrderTypeId: string, request: MarketOrderType, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true);
  }

  // Market Suppliers (stub implementations)
  async getMarketSupplier(supplierId: string, cancelToken?: CancelToken): Promise<Result<MarketSupplier>> {
    await delay();
    return success({} as MarketSupplier);
  }

  async getMarketSuppliers(options: MarketSupplierFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<MarketSupplier>>> {
    await delay();
    
    const mockSuppliers: MarketSupplier[] = [
      {
        supplierId: 1,
        instanceName: 'Chick-fil-A',
        companyId: 1,
        marketName: 'Ireland',
        customerTypeId: 1,
        supplierNumber: 'SUP001',
        supplierName: 'Fresh Foods Ltd',
        address1: '123 Industrial Estate',
        address2: 'North Road',
        town: 'Dublin',
        county: 'Dublin',
        postcode: 'D11 ABC1',
        country: 'Ireland',
        emailAddress: 'orders@freshfoods.ie',
        emailAddress2: 'accounts@freshfoods.ie',
        fileFormat: 'CSV',
        publishMethod: 'Email',
        supplierNumber2: '',
        cutOffTime: '14:00',
        creditRequestCutOffTime: '16:00',
      },
      {
        supplierId: 2,
        instanceName: 'Chick-fil-A',
        companyId: 1,
        marketName: 'Ireland',
        customerTypeId: 1,
        supplierNumber: 'SUP002',
        supplierName: 'Ice Cold Distributors',
        address1: '456 Cold Storage Lane',
        address2: '',
        town: 'Cork',
        county: 'Cork',
        postcode: 'T12 XYZ2',
        country: 'Ireland',
        emailAddress: 'orders@icecold.ie',
        emailAddress2: '',
        fileFormat: 'XML',
        publishMethod: 'FTP',
        supplierNumber2: '',
        cutOffTime: '12:00',
        creditRequestCutOffTime: '14:00',
      },
      {
        supplierId: 3,
        instanceName: 'Chick-fil-A',
        companyId: 1,
        marketName: 'Ireland',
        customerTypeId: 1,
        supplierNumber: 'SUP003',
        supplierName: 'Ambient Supplies Co',
        address1: '789 Warehouse Drive',
        address2: 'Unit 5',
        town: 'Galway',
        county: 'Galway',
        postcode: 'H91 DEF3',
        country: 'Ireland',
        emailAddress: 'orders@ambient.ie',
        emailAddress2: 'support@ambient.ie',
        fileFormat: 'CSV',
        publishMethod: 'Email',
        supplierNumber2: 'ALT003',
        cutOffTime: '16:00',
        creditRequestCutOffTime: '18:00',
      },
      {
        supplierId: 4,
        instanceName: 'Orderly Demo',
        companyId: 2,
        marketName: 'Germany',
        customerTypeId: 3,
        supplierNumber: 'SUP101',
        supplierName: 'Demo Supplier GmbH',
        address1: 'Industriestraße 123',
        address2: '',
        town: 'Berlin',
        county: 'Berlin',
        postcode: '10115',
        country: 'Germany',
        emailAddress: 'orders@demo.de',
        emailAddress2: 'info@demo.de',
        fileFormat: 'CSV',
        publishMethod: 'Email',
        supplierNumber2: '',
        cutOffTime: '15:00',
        creditRequestCutOffTime: '17:00',
      },
    ];
    
    let filtered = [...mockSuppliers];
    if (options.companyId) {
      filtered = filtered.filter(s => s.companyId === parseInt(options.companyId!));
    }
    if (options.customerTypeId) {
      filtered = filtered.filter(s => s.customerTypeId === parseInt(options.customerTypeId!));
    }
    
    const start = (options.pageNumber ?? 0) * (options.pageSize ?? 10);
    const end = start + (options.pageSize ?? 10);
    const items = filtered.slice(start, end);
    
    return success({
      items,
      pageNumber: options.pageNumber ?? 0,
      totalPages: Math.ceil(filtered.length / (options.pageSize ?? 10)),
      totalCount: filtered.length,
      hasPreviousPage: (options.pageNumber ?? 0) > 0,
      hasNextPage: end < filtered.length,
      sort: options.sort ?? '',
      direction: options.direction ?? '',
    });
  }

  async createMarketSupplier(request: MarketSupplier, cancelToken?: CancelToken): Promise<Result<number>> {
    await delay();
    return success(1);
  }

  async updateMarketSupplier(supplierId: string, request: MarketSupplier, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true);
  }

  // Market Catalogues (stub implementations)
  async getMarketCatalogue(catalogueId: string, cancelToken?: CancelToken): Promise<Result<MarketCatalogue>> {
    await delay();
    return success({} as MarketCatalogue);
  }

  async getMarketCatalogues(options: MarketCatalogueFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<MarketCatalogue>>> {
    await delay();
    
    const mockCatalogues: MarketCatalogue[] = [
      {
        customerGroupId: 1,
        instanceName: 'Chick-fil-A',
        companyId: 1,
        name: 'Fresh & Chilled - Ireland',
        message: 'Fresh and chilled products for daily delivery',
        marketName: 'Ireland',
        customerTypeId: 1,
        orderTypeIds: [1],
        supplierIds: [1],
      },
      {
        customerGroupId: 2,
        instanceName: 'Chick-fil-A',
        companyId: 1,
        name: 'Frozen Products - Ireland',
        message: 'Frozen food items',
        marketName: 'Ireland',
        customerTypeId: 1,
        orderTypeIds: [2],
        supplierIds: [2],
      },
      {
        customerGroupId: 3,
        instanceName: 'Chick-fil-A',
        companyId: 1,
        name: 'Ambient Stock - Ireland',
        message: 'Dry goods and ambient products',
        marketName: 'Ireland',
        customerTypeId: 1,
        orderTypeIds: [3],
        supplierIds: [3],
      },
      {
        customerGroupId: 4,
        instanceName: 'Orderly Demo',
        companyId: 2,
        name: 'Daily Products Catalogue',
        message: 'Main product catalogue for daily orders',
        marketName: 'Germany',
        customerTypeId: 3,
        orderTypeIds: [4],
        supplierIds: [4],
      },
    ];
    
    let filtered = [...mockCatalogues];
    if (options.companyId) {
      filtered = filtered.filter(c => c.companyId === parseInt(options.companyId!));
    }
    if (options.customerTypeId) {
      filtered = filtered.filter(c => c.customerTypeId === parseInt(options.customerTypeId!));
    }
    
    const start = (options.pageNumber ?? 0) * (options.pageSize ?? 10);
    const end = start + (options.pageSize ?? 10);
    const items = filtered.slice(start, end);
    
    return success({
      items,
      pageNumber: options.pageNumber ?? 0,
      totalPages: Math.ceil(filtered.length / (options.pageSize ?? 10)),
      totalCount: filtered.length,
      hasPreviousPage: (options.pageNumber ?? 0) > 0,
      hasNextPage: end < filtered.length,
      sort: options.sort ?? '',
      direction: options.direction ?? '',
    });
  }

  async createMarketCatalogue(request: MarketCatalogue, cancelToken?: CancelToken): Promise<Result<number>> {
    await delay();
    return success(1);
  }

  async updateMarketCatalogue(catalogueId: string, request: MarketCatalogue, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true);
  }

  // Market Stores (stub implementations)
  async getMarketStore(storeId: string, cancelToken?: CancelToken): Promise<Result<MarketStore>> {
    await delay();
    return success({} as MarketStore);
  }

  async getMarketStores(options: MarketStoreFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<MarketStore>>> {
    await delay();
    
    const mockStores: MarketStore[] = [
      {
        customerId: 1,
        companyId: 1,
        instanceName: 'Chick-fil-A',
        customerTypeId: 1,
        marketName: 'Ireland',
        storeNumber: '001',
        storeName: 'Dublin Flagship',
        emailAddress: 'dublin@cfa.ie',
        phoneNumber: '+353 1 234 5678',
        shipToLocation: 'Dublin Flagship',
        shipToAddress: '123 Grafton Street',
        town: 'Dublin',
        county: 'Dublin',
        country: 'Ireland',
        postCode: 'D02 X123',
        catalogueIds: [],
        odsConfiguration: [],
      },
      {
        customerId: 2,
        companyId: 1,
        instanceName: 'Chick-fil-A',
        customerTypeId: 1,
        marketName: 'Ireland',
        storeNumber: '002',
        storeName: 'Cork Central',
        emailAddress: 'cork@cfa.ie',
        phoneNumber: '+353 21 234 5678',
        shipToLocation: 'Cork Central',
        shipToAddress: '45 Patrick Street',
        town: 'Cork',
        county: 'Cork',
        country: 'Ireland',
        postCode: 'T12 X456',
        catalogueIds: [],
        odsConfiguration: [],
      },
      {
        customerId: 3,
        companyId: 1,
        instanceName: 'Chick-fil-A',
        customerTypeId: 1,
        marketName: 'Ireland',
        storeNumber: '003',
        storeName: 'Galway Bay',
        emailAddress: 'galway@cfa.ie',
        phoneNumber: '+353 91 234 5678',
        shipToLocation: 'Galway Bay',
        shipToAddress: '78 Shop Street',
        town: 'Galway',
        county: 'Galway',
        country: 'Ireland',
        postCode: 'H91 X789',
        catalogueIds: [],
        odsConfiguration: [],
      },
      {
        customerId: 4,
        companyId: 1,
        instanceName: 'Chick-fil-A',
        customerTypeId: 2,
        marketName: 'United Kingdom',
        storeNumber: '004',
        storeName: 'Belfast City',
        emailAddress: 'belfast@cfa.uk',
        phoneNumber: '+44 28 1234 5678',
        shipToLocation: 'Belfast City',
        shipToAddress: '12 High Street',
        town: 'Belfast',
        county: 'County Antrim',
        country: 'United Kingdom',
        postCode: 'BT1 1AA',
        catalogueIds: [],
        odsConfiguration: [],
      },
      {
        customerId: 5,
        companyId: 1,
        instanceName: 'Chick-fil-A',
        customerTypeId: 2,
        marketName: 'United Kingdom',
        storeNumber: '005',
        storeName: 'London Oxford Street',
        emailAddress: 'london@cfa.uk',
        phoneNumber: '+44 20 1234 5678',
        shipToLocation: 'London Oxford Street',
        shipToAddress: '456 Oxford Street',
        town: 'London',
        county: 'Greater London',
        country: 'United Kingdom',
        postCode: 'W1C 1AA',
        catalogueIds: [],
        odsConfiguration: [],
      },
    ];
    
    let filtered = [...mockStores];
    if (options.companyId) {
      filtered = filtered.filter(s => s.companyId === parseInt(options.companyId!));
    }
    if (options.customerTypeId) {
      filtered = filtered.filter(s => s.customerTypeId === parseInt(options.customerTypeId!));
    }
    if (options.searchTerm) {
      const term = options.searchTerm.toLowerCase();
      filtered = filtered.filter(s => 
        s.storeNumber?.toLowerCase().includes(term) ||
        s.storeName?.toLowerCase().includes(term),
      );
    }
    
    const start = (options.pageNumber ?? 0) * (options.pageSize ?? 10);
    const end = start + (options.pageSize ?? 10);
    const items = filtered.slice(start, end);
    
    return success({
      items,
      pageNumber: options.pageNumber ?? 0,
      totalPages: Math.ceil(filtered.length / (options.pageSize ?? 10)),
      totalCount: filtered.length,
      hasPreviousPage: (options.pageNumber ?? 0) > 0,
      hasNextPage: end < filtered.length,
      sort: options.sort ?? '',
      direction: options.direction ?? '',
    });
  }

  async createMarketStore(request: MarketStore, cancelToken?: CancelToken): Promise<Result<number>> {
    await delay();
    return success(1);
  }

  async updateMarketStore(storeId: string, request: MarketStore, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true);
  }

  async downloadMarketStores(companyId: string, customerTypeId: string, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    await delay();
    return success({ fileData: '', fileName: '' });
  }

  async uploadMarketStores(request: MarketStoreUploadRequest, cancelToken?: CancelToken): Promise<Result<MarketStoreUploadResult>> {
    await delay();
    return success({ success: true, message: '', rowsProcessed: 0, created: 0, updated: 0, errors: [] });
  }

  // UOM Types (stub implementations)
  async getUomType(uomTypeId: string, cancelToken?: CancelToken): Promise<Result<UomType>> {
    await delay();
    return success({} as UomType);
  }

  async getUomTypes(options: UomTypeFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<UomType>>> {
    await delay();
    return success({
      items: [],
      pageNumber: options.pageNumber,
      totalPages: 0,
      totalCount: 0,
      hasPreviousPage: false,
      hasNextPage: false,
      sort: options.sort ?? '',
      direction: options.direction ?? '',
    });
  }

  async createUomType(request: UomType, cancelToken?: CancelToken): Promise<Result<number>> {
    await delay();
    return success(1);
  }

  async updateUomType(uomTypeId: string, request: UomType, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true);
  }

  // Market Categories (stub implementations)
  async getMarketCategory(categoryId: string, cancelToken?: CancelToken): Promise<Result<MarketCategory>> {
    await delay();
    return success({} as MarketCategory);
  }

  async getMarketCategories(options: MarketCategoryFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<MarketCategory>>> {
    await delay();
    return success({
      items: [],
      pageNumber: options.pageNumber,
      totalPages: 0,
      totalCount: 0,
      hasPreviousPage: false,
      hasNextPage: false,
      sort: options.sort ?? '',
      direction: options.direction ?? '',
    });
  }

  async createMarketCategory(request: MarketCategory, cancelToken?: CancelToken): Promise<Result<number>> {
    await delay();
    return success(1);
  }

  async updateMarketCategory(categoryId: string, request: MarketCategory, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true);
  }

  // Market Items (stub implementations)
  async getMarketItem(itemId: string, companyId: string, customerTypeId: string, cancelToken?: CancelToken): Promise<Result<MarketItem>> {
    await delay();
    return success({} as MarketItem);
  }

  async getMarketItems(options: MarketItemFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<MarketItem>>> {
    await delay();
    return success({
      items: [],
      pageNumber: options.pageNumber,
      totalPages: 0,
      totalCount: 0,
      hasPreviousPage: false,
      hasNextPage: false,
      sort: options.sort ?? '',
      direction: options.direction ?? '',
    });
  }

  async createMarketItem(request: MarketItem, cancelToken?: CancelToken): Promise<Result<number>> {
    await delay();
    return success(1);
  }

  async updateMarketItem(itemId: string, request: MarketItem, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true);
  }

  async downloadMarketItems(companyId: string, customerTypeId: string, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    await delay();
    return success({ fileData: '', fileName: '' });
  }

  async uploadMarketItems(request: MarketItemUploadRequest, cancelToken?: CancelToken): Promise<Result<MarketItemUploadResult>> {
    await delay();
    return success({ success: true, message: '', rowsProcessed: 0, created: 0, updated: 0, errors: [] });
  }

  // Autoship Templates (stub implementations)
  async createOrUpdateAutoshipTemplate(request: AutoshipTemplate, storeNumber: string, cancelToken?: CancelToken): Promise<Result<number>> {
    await delay();
    return success(1);
  }

  async getAutoshipTemplates(options: AutoshipmentTemplatesFilterOptions, storeNumber: string, cancelToken?: CancelToken): Promise<Result<PaginatedList<AutoshipTemplateModel>>> {
    await delay();
    return success({
      items: [],
      pageNumber: options.pageNumber,
      totalPages: 0,
      totalCount: 0,
      hasPreviousPage: false,
      hasNextPage: false,
      sort: options.sort ?? '',
      direction: options.direction ?? '',
    });
  }

  async getAutoshipTemplateById(id: string, storeNumber: string, includeExpandedDetails?: boolean, cancelToken?: CancelToken): Promise<Result<AutoshipTemplateModel>> {
    await delay();
    return success({} as AutoshipTemplateModel);
  }

  async getAutoshipTemplateNames(storeNumber: string, cancelToken?: CancelToken): Promise<Result<string[]>> {
    await delay();
    return success([]);
  }

  async downloadItemsCsv(storeNumber: string, id: string, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    await delay();
    return success({ fileData: '', fileName: '' });
  }

  async uploadTemplateItemsCsv(storeNumber: string | undefined, orderTypeId: number, request: FileRequest, cancelToken?: CancelToken): Promise<Result<Item[]>> {
    await delay();
    return success([]);
  }

  async archiveTemplate(storeNumber: string, templateId: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true);
  }

  async deleteTemplate(storeNumber: string, templateId: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true);
  }

  // Autoship Schedules (stub implementations)
  async createOrUpdateAutoshipSchedule(request: AutoshipSchedule, storeNumber: string, cancelToken?: CancelToken): Promise<Result<number>> {
    await delay();
    return success(1);
  }

  async getAutoshipSchedules(options: AutoshipmentSchedulesFilterOptions, storeNumber: string, cancelToken?: CancelToken): Promise<Result<PaginatedList<AutoshipScheduleModel>>> {
    await delay();
    return success({
      items: [],
      pageNumber: options.pageNumber,
      totalPages: 0,
      totalCount: 0,
      hasPreviousPage: false,
      hasNextPage: false,
      sort: options.sort ?? '',
      direction: options.direction ?? '',
    });
  }

  async getAutoshipScheduleById(id: number, storeNumber: string, cancelToken?: CancelToken): Promise<Result<AutoshipScheduleModel>> {
    await delay();
    return success({} as AutoshipScheduleModel);
  }

  async deleteSchedule(storeNumber: string, scheduleId: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    return success(true);
  }

  async getDeliverySchedulesForStores(companyId: number, orderTypeId: number, stores: string, cancelToken?: CancelToken): Promise<Result<StoreAvailableODS[]>> {
    await delay();
    
    // Parse stores parameter (comma-separated string)
    const storeNumbers = stores.split(',').map(s => s.trim()).filter(s => s.length > 0);
    
    // Filter ODS data by requested stores
    let filteredODS = mockStoreAvailableODS;
    if (storeNumbers.length > 0) {
      filteredODS = mockStoreAvailableODS.filter(ods => storeNumbers.includes(ods.storeNumber));
    }
    
    return success(filteredODS);
  }

  async getAutoshipReasons(storeNumber: string, cancelToken?: CancelToken): Promise<Result<IdNameModel[]>> {
    await delay();
    return success([]);
  }

  // EDN Orders (stub implementation)
  async getOrderEdn(storeNumber?: string, generateNew?: boolean, orderId?: string, cancelToken?: CancelToken): Promise<RetailerEdnOrderModel> {
    await delay();
    return {} as RetailerEdnOrderModel;
  }

  // ODS - Order Delivery Schedule Methods
  async getODS(options: DeliveryScheduleFilterOptions, storeNumber: string, cancelToken?: CancelToken): Promise<Result<PaginatedList<DeliveryScheduleModel>>> {
    await delay();
    
    // Mock ODS delivery schedule data
    const mockODSData: DeliveryScheduleModel[] = [
      {
        id: 1,
        customerId: 1001,
        storeNumber: '001',
        storeName: 'Dublin Delights Café',
        orderTypeId: 1,
        orderType: 'Fresh & Daily',
        cutOffDate: '2025-12-13',
        deliveryDate: '2025-12-15',
        cutOffTime: '18:00',
        supplierNumber: 'SUP001',
        supplierName: 'Fresh Foods Ltd',
        rowNumber: 1,
      },
      {
        id: 2,
        customerId: 1001,
        storeNumber: '001',
        storeName: 'Dublin Delights Café',
        orderTypeId: 2,
        orderType: 'Ambient',
        cutOffDate: '2025-12-14',
        deliveryDate: '2025-12-18',
        cutOffTime: '12:00',
        supplierNumber: 'SUP002',
        supplierName: 'Ambient Supplies Co',
        rowNumber: 2,
      },
      {
        id: 3,
        customerId: 1002,
        storeNumber: '002',
        storeName: 'Cork Coffee House',
        orderTypeId: 1,
        orderType: 'Fresh & Daily',
        cutOffDate: '2025-12-12',
        deliveryDate: '2025-12-14',
        cutOffTime: '18:00',
        supplierNumber: 'SUP001',
        supplierName: 'Fresh Foods Ltd',
        rowNumber: 3,
      },
      {
        id: 4,
        customerId: 1003,
        storeNumber: '003',
        storeName: 'Galway Gourmet',
        orderTypeId: 3,
        orderType: 'Frozen',
        cutOffDate: '2025-12-10',
        deliveryDate: '2025-12-13',
        cutOffTime: '14:00',
        supplierNumber: 'SUP003',
        supplierName: 'Arctic Frozen Foods',
        rowNumber: 4,
      },
    ];

    // Apply filters
    let filteredData = [...mockODSData];
    
    if (options.stores) {
      const storeList = options.stores.split(',').map(s => s.trim());
      filteredData = filteredData.filter(ods => storeList.includes(ods.storeNumber));
    }
    
    if (options.orderTypes) {
      const orderTypeList = options.orderTypes.split(',').map(ot => parseInt(ot.trim()));
      filteredData = filteredData.filter(ods => orderTypeList.includes(ods.orderTypeId));
    }
    
    if (options.supplierNumber) {
      filteredData = filteredData.filter(ods => ods.supplierNumber === options.supplierNumber);
    }

    // Pagination
    const startIndex = (options.pageNumber - 1) * options.pageSize;
    const endIndex = startIndex + options.pageSize;
    const paginatedItems = filteredData.slice(startIndex, endIndex);
    const totalCount = filteredData.length;
    const totalPages = Math.ceil(totalCount / options.pageSize);

    return success({
      items: paginatedItems,
      pageNumber: options.pageNumber,
      totalPages,
      totalCount,
      hasPreviousPage: options.pageNumber > 1,
      hasNextPage: options.pageNumber < totalPages,
      sort: options.sort ?? '',
      direction: options.direction ?? '',
    });
  }

  async exportODS(options: DeliveryScheduleFilterOptions, storeNumber: string, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    await delay();
    // Mock Excel file export
    const mockBase64Excel = 'UEsDBBQABgAIAAAAIQDfpNJsWgEAACAFAAATAAgCW0NvbnRlbnRfVHlwZXNdLnhtbCCiBAIooAAC';
    return success({
      fileData: mockBase64Excel,
      fileName: `ODS_Export_${new Date().toISOString().split('T')[0]}.xlsx`,
    });
  }

  async validateODS(request: FileRequest, storeNumber: string, instanceId: number, marketId: number, cancelToken?: CancelToken): Promise<Result<ODSImportResultModel>> {
    await delay();
    
    // Mock validation result
    const mockValidationResult: ODSImportResultModel = {
      created: [
        {
          id: 0,
          customerId: 1001,
          storeNumber: '001',
          storeName: 'Dublin Delights Café',
          orderTypeId: 1,
          orderType: 'Fresh & Daily',
          cutOffDate: '2025-12-20',
          deliveryDate: '2025-12-22',
          cutOffTime: '18:00',
          supplierNumber: 'SUP001',
          supplierName: 'Fresh Foods Ltd',
          rowNumber: 1,
        },
      ],
      updated: [
        {
          id: 2,
          customerId: 1001,
          storeNumber: '001',
          storeName: 'Dublin Delights Café',
          orderTypeId: 2,
          orderType: 'Ambient',
          cutOffDate: '2025-12-21',
          deliveryDate: '2025-12-25',
          cutOffTime: '12:00',
          supplierNumber: 'SUP002',
          supplierName: 'Ambient Supplies Co',
          rowNumber: 2,
        },
      ],
      deleted: [],
      invalid: [],
    };

    return success(mockValidationResult);
  }

  async importODS(request: FileRequest, storeNumber: string, instanceId: number, marketId: number, languageCode: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    // Simulate successful import after validation
    return success(true);
  }

  async updateBulkODSDate(request: BulkODSDateUpdateModel, storeNumber: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    // Simulate bulk date update success
    console.log(`Mock: Updating ${request.ids.length} ODS entries with date: ${request.date}, isCutOffDate: ${request.isCutOffDate}`);
    return success(true);
  }

  async deleteBulkODS(ids: number[], storeNumber: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    // Simulate bulk deletion success
    console.log(`Mock: Deleting ${ids.length} ODS entries`);
    return success(true);
  }

  async addODS(request: AddODSModel, storeNumber: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    await delay();
    // Simulate ODS creation success
    console.log(`Mock: Creating ODS for ${request.storeNumbers.length} stores, orderType: ${request.orderTypeId}, deliveryDate: ${request.deliveryDate}`);
    return success(true);
  }

  // Reports
  async generateForecastOrdersReport(storeNumber: string, request: ForecastOrdersReportRequest, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    await delay();
    // Mock Excel report generation
    const mockBase64Excel = 'UEsDBBQABgAIAAAAIQDfpNJsWgEAACAFAAATAAgCW0NvbnRlbnRfVHlwZXNdLnhtbCCiBAIooAAC';
    const fileName = `Forecast_Orders_Report_${request.fromDate}_to_${request.toDate}.xlsx`;
    return success({
      fileData: mockBase64Excel,
      fileName,
    });
  }
}
